import React, { useState, useEffect, useMemo, useCallback, useRef, useContext } from 'react';
import {
  Box,
  Container,
  Card,
  CardContent,
  Typography,
  Button,
  IconButton,
  Stack,
  TextField,
  Grid,
  Chip,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress,
  alpha,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  Backdrop,
  CircularProgress,
  InputAdornment,
  Tooltip,
  useTheme,
  Autocomplete,
  Fade,
  Zoom
} from '@mui/material';
import {
  History as HistoryIcon,
  PictureAsPdf as PdfIcon,
  FilterList as FilterIcon,
  Search as SearchIcon,
  CalendarToday,
  Refresh as RefreshIcon,
  Print as PrintIcon,
  Cancel as CancelIcon,
  Visibility as ViewIcon,
  Download as DownloadIcon,
  Close as CloseIcon,
  CalendarToday as CalendarIcon,
  Clear as ClearIcon,
  TrendingUp as TrendingUpIcon,
  Payment as PaymentIcon,
  Receipt as ReceiptIcon,
  Scale as ScaleIcon,
  Diamond as DiamondIcon,
  Inventory as InventoryIcon,
  Share as ShareIcon,
  ArrowUpward as ArrowUpwardIcon,
  CheckCircle as CheckCircleIcon,
  Pending as PendingIcon,
  ArrowForward as ArrowForwardIcon,
  Person as PersonIcon,
  LocalAtm as LocalAtmIcon,
  AccountBalance as AccountBalanceIcon,
  Paid as PaidIcon,
  Discount as DiscountIcon,
  ErrorOutline as ErrorOutlineIcon,
  CheckCircleOutline as CheckCircleOutlineIcon,
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { format, parse } from 'date-fns';
import { AgGridReact } from 'ag-grid-react';
import { AllCommunityModule, ModuleRegistry } from 'ag-grid-community';
import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';
import * as XLSX from 'xlsx';
import { DataContext } from '../../context/DataContext';
import {
  generateTransactionBillHTML,
  getBillStyles
} from '../../components/billPrintUtils';
import '../../styles/ag-grid-premium-theme.css'

ModuleRegistry.registerModules([AllCommunityModule]);

// ================================================================
// CUSTOM HOOKS
// ================================================================
const useDebounce = (value, delay = 300) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => clearTimeout(handler);
  }, [value, delay]);

  return debouncedValue;
};

// ================================================================
// HELPERS
// ================================================================
const isCancelledTransaction = (transaction) => {
  return (
    transaction?.transactionDetails?.type === "cancelled" ||
    transaction?.status === "cancelled" ||
    transaction?.isCancelled === true ||
    transaction?.transactionDetails?.isCancelled === true ||
    transaction?.cancelledAt !== undefined
  );
};

// const extractFineData = (transaction) => {
//   if (!transaction) {
//     return {
//       fineRequired: 0,
//       finePaid: 0,
//       fineBalance: 0,
//       hasFine: false,
//       fineEntries: [],
//     };
//   }

//   const fineRequired =
//     transaction.transactionDetails?.finePayment?.requiredFine ||
//     transaction.summary?.fineRequired ||
//     transaction.paymentDetails?.finePayment?.totalFineRequired ||
//     0;

//   const finePaid =
//     transaction.transactionDetails?.finePayment?.totalFinePaid ||
//     transaction.summary?.finePaid ||
//     transaction.paymentDetails?.finePayment?.totalFinePaid ||
//     0;

//   const fineBalance =
//     transaction.transactionDetails?.finePayment?.balanceFine ??
//     transaction.summary?.fineBalance ??
//     transaction.paymentDetails?.finePayment?.balanceFine ??
//     0;

//   const hasFine = fineRequired > 0 || finePaid > 0;

//   // Collect fine entries from all sources
//   const paymentHistoryFines = (transaction.paymentDetails?.paymentHistory || []).flatMap((p) => {
//     const fines = [];
//     if (p.paymentModes?.fine) {
//       fines.push({
//         ...p.paymentModes.fine,
//         date: p.date,
//         recordedBy: p.recordedBy,
//       });
//     }
//     if (Array.isArray(p.paymentModes?.fineEntries)) {
//       p.paymentModes.fineEntries.forEach((f) => {
//         fines.push({
//           ...f,
//           date: p.date,
//           recordedBy: p.recordedBy,
//         });
//       });
//     }
//     return fines;
//   });

//   const combinedFineEntries = [
//     ...(transaction.paymentDetails?.finePayment?.entries || []),
//     ...(transaction.transactionDetails?.finePayment?.entries || []),
//     ...paymentHistoryFines,
//   ];

//   const fineEntries = combinedFineEntries.filter((entry, index, arr) => {
//     return (
//       arr.findIndex(
//         (e) =>
//           e.metal === entry.metal &&
//           e.weight === entry.weight &&
//           e.purity === entry.purity
//       ) === index
//     );
//   });

//   return {
//     fineRequired,
//     finePaid,
//     fineBalance,
//     hasFine,
//     fineEntries,
//   };
// };

const isMobileDevice = () => {
  const isMobileUA = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  const isSmallScreen = window.innerWidth < 768;
  return isMobileUA || isSmallScreen;
};

// ================================================================
// CELL RENDERERS (AG GRID)
// ================================================================
const StatusRenderer = (props) => {
  const cancelled = isCancelledTransaction(props.data);

  if (cancelled) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <Chip
          label="CANCELLED"
          size="small"
          color="error"
          variant="filled"
          sx={{
            fontWeight: 700,
            borderRadius: '8px',
            minWidth: '100px',
            backdropFilter: 'blur(10px)',
            backgroundColor: 'rgba(220, 38, 38, 0.15)',
            border: '1px solid rgba(220, 38, 38, 0.3)',
            textDecoration: 'line-through',
            '& .MuiChip-label': {
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
            }
          }}
          icon={<CancelIcon sx={{ fontSize: '14px', color: '#DC2626' }} />}
        />
      </Box>
    );
  }

  const pendingAmount = parseInt(parseFloat(props.data?.summary?.pendingAmount).toFixed(2)) || 0;

  const fineBalance =
    props.data?.transactionDetails?.finePayment?.balanceFine ??
    props.data?.summary?.fineBalance ??
    props.data?.paymentDetails?.finePayment?.balanceFine ??
    0;

  const isPaid = pendingAmount === 0 && fineBalance === 0;

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
      <Chip
        label={isPaid ? 'Paid' : 'Pending'}
        size="small"
        color={isPaid ? 'success' : 'warning'}
        variant="filled"
        sx={{
          fontWeight: 600,
          borderRadius: '8px',
          minWidth: '80px',
          backdropFilter: 'blur(10px)',
          backgroundColor: isPaid
            ? 'rgba(16, 185, 129, 0.15)'
            : 'rgba(245, 158, 11, 0.15)',
          border: isPaid
            ? '1px solid rgba(16, 185, 129, 0.3)'
            : '1px solid rgba(245, 158, 11, 0.3)',
          '& .MuiChip-label': {
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
          }
        }}
        icon={
          isPaid ? (
            <CheckCircleIcon sx={{ fontSize: '14px', color: '#10B981' }} />
          ) : (
            <PendingIcon sx={{ fontSize: '14px', color: '#F59E0B' }} />
          )
        }
      />
    </Box>
  );
};

const AmountRenderer = (props) => {
  const value = props.value || 0;

  return (
    <Box sx={{
      textAlign: 'right',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'flex-end',
    }}>
      <Typography
        variant="body2"
        sx={{
          fontWeight: 600,
          background: 'linear-gradient(135deg, #2563EB, #3B82F6)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          backgroundClip: 'text',
        }}
      >
        {new Intl.NumberFormat('en-IN', {
          style: 'currency',
          currency: 'INR',
          minimumFractionDigits: 0,
        }).format(value)}
      </Typography>
      {props.data?.summary?.pendingAmount > 0 && props.field === 'summary.grandTotal' && (
        <Typography
          variant="caption"
          sx={{
            color: '#EF4444',
            fontSize: '0.7rem',
            fontWeight: 500,
            display: 'flex',
            alignItems: 'center',
            gap: '2px',
            mt: '2px',
          }}
        >
          <ArrowUpwardIcon sx={{ fontSize: '10px' }} />
          {(props.data.summary.pendingAmount / value * 100).toFixed(1)}% due
        </Typography>
      )}
    </Box>
  );
};

const FineRenderer = (props) => {
  const transaction = props.data;
  const fineRequired =
    transaction?.transactionDetails?.finePayment?.requiredFine ||
    transaction?.summary?.fineRequired ||
    transaction?.paymentDetails?.finePayment?.totalFineRequired ||
    0;
  const finePaid =
    transaction?.transactionDetails?.finePayment?.totalFinePaid ||
    transaction?.summary?.finePaid ||
    transaction?.paymentDetails?.finePayment?.totalFinePaid ||
    0;
  const fineBalance =
    transaction?.transactionDetails?.finePayment?.balanceFine ??
    transaction?.summary?.fineBalance ??
    transaction?.paymentDetails?.finePayment?.balanceFine ??
    0;

  if (fineRequired === 0 && finePaid === 0) {
    return (
      <Box sx={{ textAlign: 'center' }}>
        <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '0.75rem' }}>
          —
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 0.5,
      }}
    >
      <Chip
        icon={<DiamondIcon sx={{ fontSize: '14px !important' }} />}
        label={`${finePaid.toFixed(3)}g / ${fineRequired.toFixed(3)}g`}
        size="small"
        variant="filled"
        sx={{
          background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.15), rgba(167, 139, 250, 0.15))',
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(139, 92, 246, 0.3)',
          color: '#8B5CF6',
          fontWeight: 700,
          borderRadius: '8px',
          fontSize: '0.7rem',
          height: 24,
          '& .MuiChip-label': {
            px: 0.75,
          },
        }}
      />
      {fineBalance > 0 && (
        <Typography
          variant="caption"
          sx={{
            color: '#EF4444',
            fontSize: '0.65rem',
            fontWeight: 600,
          }}
        >
          Bal: {fineBalance.toFixed(3)}g
        </Typography>
      )}
    </Box>
  );
};

const CustomerNameRenderer = (props) => {
  const { customers } = props.context;
  const customer = customers.find(c => c.id === props.value);

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <Box sx={{
        width: 36,
        height: 36,
        borderRadius: '10px',
        background: 'linear-gradient(135deg, #8B5CF6, #A78BFA)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: 'white',
        fontWeight: 600,
        fontSize: '0.875rem',
        boxShadow: '0 2px 8px rgba(139, 92, 246, 0.3)',
      }}>
        {customer?.name?.charAt(0) || 'C'}
      </Box>
      <Box>
        <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
          {customer?.name || 'N/A'}
        </Typography>
        {customer?.contact && (
          <Typography variant="caption" sx={{
            color: 'text.secondary',
            display: 'flex',
            alignItems: 'center',
            gap: '2px',
            fontSize: '0.7rem',
          }}>
            {customer.contact}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

const ItemsRenderer = (props) => {
  const itemsCount = props.value?.length || 0;

  return (
    <Box sx={{ textAlign: 'center', display: 'flex', justifyContent: 'center' }}>
      <Chip
        label={`${itemsCount} item${itemsCount !== 1 ? 's' : ''}`}
        size="small"
        variant="filled"
        sx={{
          background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.15), rgba(167, 139, 250, 0.15))',
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(139, 92, 246, 0.3)',
          color: '#8B5CF6',
          fontWeight: 600,
          borderRadius: '8px',
          minWidth: '70px',
          '&:hover': {
            background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.25), rgba(167, 139, 250, 0.25))',
          },
        }}
        icon={<InventoryIcon sx={{ fontSize: '14px', color: '#8B5CF6' }} />}
      />
    </Box>
  );
};

// ================================================================
// TRANSACTION DETAIL MODAL (SEPARATE COMPONENT)
// ================================================================
const TransactionDetailModal = ({
  open,
  onClose,
  transaction,
  customers,
  isDark,
}) => {
  // const theme = useTheme();

  // ============ FINE DATA (more robust extraction) ============
  const fineData = useMemo(() => {
    if (!transaction) {
      return { fineRequired: 0, finePaid: 0, fineBalance: 0, hasFine: false, fineEntries: [] };
    }

    const fineRequired =
      transaction.transactionDetails?.finePayment?.requiredFine ||
      transaction.summary?.fineRequired ||
      transaction.paymentDetails?.finePayment?.totalFineRequired ||
      0;

    // Prefer summing from payment history (source of truth) — fall back to stored totals
    const paymentHistoryFineSum = (transaction.paymentDetails?.paymentHistory || []).reduce(
      (sum, p) => {
        // Case 1: single fine object
        if (p.paymentModes?.fine?.weight && p.paymentModes?.fine?.purity) {
          sum +=
            (parseFloat(p.paymentModes.fine.weight) || 0) *
            (parseFloat(p.paymentModes.fine.purity) || 0) /
            100;
        }
        // Case 2: multi-entry fine array
        if (Array.isArray(p.paymentModes?.fineEntries)) {
          p.paymentModes.fineEntries.forEach((f) => {
            sum +=
              (parseFloat(f.weight) || 0) *
              (parseFloat(f.purity) || 0) /
              100;
          });
        }
        return sum;
      },
      0
    );

    const storedFinePaid =
      transaction.summary?.finePaid ??
      transaction.transactionDetails?.finePayment?.totalFinePaid ??
      transaction.paymentDetails?.finePayment?.totalFinePaid ??
      0;

    const finePaid = Math.max(paymentHistoryFineSum, storedFinePaid);

    const fineBalance =
      transaction.transactionDetails?.finePayment?.balanceFine ??
      transaction.summary?.fineBalance ??
      transaction.paymentDetails?.finePayment?.balanceFine ??
      Math.max(0, fineRequired - finePaid);

    const hasFine = fineRequired > 0 || finePaid > 0;

    // Collect all fine entries from every possible source
    const paymentHistoryFines = (transaction.paymentDetails?.paymentHistory || []).flatMap((p) => {
      const fines = [];
      if (p.paymentModes?.fine?.weight) {
        fines.push({
          ...p.paymentModes.fine,
          date: p.date,
          recordedBy: p.recordedBy,
        });
      }
      if (Array.isArray(p.paymentModes?.fineEntries)) {
        p.paymentModes.fineEntries.forEach((f) => {
          if (f.weight) {
            fines.push({
              ...f,
              date: p.date,
              recordedBy: p.recordedBy,
            });
          }
        });
      }
      return fines;
    });

    const combinedFineEntries = [
      ...(transaction.paymentDetails?.finePayment?.entries || []),
      ...(transaction.transactionDetails?.finePayment?.entries || []),
      ...paymentHistoryFines,
    ];

    // Dedupe by metal+weight+purity
    const fineEntries = combinedFineEntries.filter((entry, index, arr) => {
      return (
        arr.findIndex(
          (e) =>
            e.metal === entry.metal &&
            e.weight === entry.weight &&
            e.purity === entry.purity
        ) === index
      );
    });

    return {
      fineRequired,
      finePaid,
      fineBalance,
      hasFine,
      fineEntries,
    };
  }, [transaction]);

  const {
    fineRequired,
    finePaid,
    fineBalance,
    hasFine,
    fineEntries,
  } = fineData;

  const getCustomerName = (customerId) => {
    const customer = customers.find(c => c.id === customerId);
    return customer?.name || 'N/A';
  };

  // ============ COMPUTE TOTAL NET WEIGHT FROM ITEMS ============
  // Because `calculations.totalNetWeight` may not be saved on old transactions
  const totalNetWeight = useMemo(() => {
    if (!transaction?.items) return 0;
    return transaction.items.reduce((sum, item) => {
      const weight = parseFloat(item.weight) || 0;
      const isPerPieceItem = item.isPerPieceItem || (weight === 0 && parseFloat(item.price) > 0);
      return sum + (isPerPieceItem ? 0 : weight);
    }, 0);
  }, [transaction]);

  if (!transaction) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xl"
      fullWidth
      TransitionComponent={Zoom}
      TransitionProps={{ timeout: 300 }}
      PaperProps={{
        sx: {
          borderRadius: '24px',
          background: isDark
            ? 'linear-gradient(135deg, rgba(15, 23, 42, 0.98) 0%, rgba(30, 41, 59, 0.98) 100%)'
            : 'linear-gradient(135deg, rgba(255, 255, 255, 0.98) 0%, rgba(248, 250, 252, 0.98) 100%)',
          backdropFilter: 'blur(30px)',
          border: `1px solid ${alpha(
            isDark ? '#475569' : '#e2e8f0',
            isDark ? 0.2 : 0.3
          )}`,
          boxShadow: '0 25px 80px rgba(0, 0, 0, 0.25)',
          maxHeight: '95vh',
          overflow: 'hidden',
        },
      }}
    >
      {/* ==================== HEADER SECTION ==================== */}
      <Box
        sx={{
          p: 2,
          borderBottom: `1px solid ${alpha(
            isDark ? '#475569' : '#e2e8f0',
            isDark ? 0.2 : 0.3
          )}`,
          background: isDark
            ? 'linear-gradient(90deg, rgba(96, 165, 250, 0.15) 0%, rgba(59, 130, 246, 0.1) 100%)'
            : 'linear-gradient(90deg, rgba(37, 99, 235, 0.08) 0%, rgba(29, 78, 216, 0.04) 100%)',
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '4px',
            background: 'linear-gradient(90deg, #60a5fa, #3b82f6, #60a5fa)',
            animation: 'shine 3s infinite linear',
          },
          '@keyframes shine': {
            '0%': { backgroundPosition: '-200px 0' },
            '100%': { backgroundPosition: 'calc(200px + 100%) 0' },
          },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            mb: 1,
          }}
        >
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
              <Box
                sx={{
                  width: 45,
                  height: 45,
                  borderRadius: '16px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: 'linear-gradient(135deg, #60A5FA 0%, #3B82F6 100%)',
                  boxShadow: '0 8px 32px rgba(96, 165, 250, 0.3)',
                }}
              >
                <ReceiptIcon sx={{ fontSize: 25, color: 'white' }} />
              </Box>
              <Box>
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 800,
                    color: isDark ? '#f1f5f9' : '#1e293b',
                    mb: 0.5,
                  }}
                >
                  Transaction Details
                </Typography>
                <Typography
                  variant="h6"
                  sx={{
                    color: isDark ? '#94a3b8' : '#64748b',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    fontWeight: 500,
                  }}
                >
                  <CalendarToday sx={{ fontSize: 18 }} />
                  {(() => {
                    try {
                      return format(
                        parse(transaction.date, 'dd/MM/yyyy', new Date()),
                        'EEEE, dd MMMM yyyy'
                      );
                    } catch {
                      return transaction.date || 'N/A';
                    }
                  })()}
                </Typography>
              </Box>
            </Box>

            {/* Bill Number & Status Chips */}
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 2 }}>
              <Chip
                icon={<ReceiptIcon />}
                label={`Bill: ${transaction.transactionDetails?.billNo || 'N/A'}`}
                sx={{
                  backgroundColor: alpha('#3b82f6', 0.15),
                  color: '#3b82f6',
                  fontWeight: 700,
                  fontSize: '0.9rem',
                  border: `1px solid ${alpha('#3b82f6', 0.3)}`,
                  backdropFilter: 'blur(10px)',
                }}
              />
              <Chip
                icon={<PersonIcon />}
                label={`Customer: ${getCustomerName(transaction.customer)}`}
                sx={{
                  backgroundColor: alpha('#10b981', 0.15),
                  color: '#10b981',
                  fontWeight: 700,
                  fontSize: '0.9rem',
                  border: `1px solid ${alpha('#10b981', 0.3)}`,
                  backdropFilter: 'blur(10px)',
                }}
              />
              <Chip
                icon={transaction.transactionDetails?.gstApplicable ? '📋' : '📄'}
                label={
                  transaction.transactionDetails?.gstApplicable
                    ? `GST: ${transaction.transactionDetails?.gstType === 'igst' ? 'IGST' : 'CGST/SGST'}`
                    : 'No GST'
                }
                sx={{
                  backgroundColor: transaction.transactionDetails?.gstApplicable
                    ? transaction.transactionDetails?.gstType === 'igst'
                      ? alpha('#f59e0b', 0.15)
                      : alpha('#059669', 0.15)
                    : alpha('#6b7280', 0.15),
                  color: transaction.transactionDetails?.gstApplicable
                    ? transaction.transactionDetails?.gstType === 'igst'
                      ? '#d97706'
                      : '#059669'
                    : '#6b7280',
                  fontWeight: 600,
                  fontSize: '0.85rem',
                  border: `1px solid ${alpha(
                    transaction.transactionDetails?.gstApplicable
                      ? transaction.transactionDetails?.gstType === 'igst'
                        ? '#f59e0b'
                        : '#059669'
                      : '#6b7280',
                    0.3
                  )}`,
                  backdropFilter: 'blur(10px)',
                }}
              />
              <Chip
                icon={
                  transaction.summary?.pendingAmount > 0 || fineBalance > 0 ? '⏳' : '✅'
                }
                label={(() => {
                  const cashPending = transaction.summary?.pendingAmount || 0;
                  if (cashPending > 0 && fineBalance > 0) {
                    return `Pending: ₹${cashPending.toLocaleString('en-IN')} + ${fineBalance.toFixed(3)}g Fine`;
                  }
                  if (cashPending > 0) {
                    return `Pending: ₹${cashPending.toLocaleString('en-IN')}`;
                  }
                  if (fineBalance > 0) {
                    return `Fine Due: ${fineBalance.toFixed(3)}g`;
                  }
                  return 'Fully Paid';
                })()}
                sx={{
                  backgroundColor:
                    transaction.summary?.pendingAmount > 0 || fineBalance > 0
                      ? alpha('#f59e0b', 0.15)
                      : alpha('#10b981', 0.15),
                  color:
                    transaction.summary?.pendingAmount > 0 || fineBalance > 0
                      ? '#f59e0b'
                      : '#10b981',
                  fontWeight: 700,
                  fontSize: '0.9rem',
                  border: `1px solid ${alpha(
                    transaction.summary?.pendingAmount > 0 || fineBalance > 0
                      ? '#f59e0b'
                      : '#10b981',
                    0.3
                  )}`,
                  backdropFilter: 'blur(10px)',
                }}
              />
              {/* FINE CHIP */}
              {hasFine && (
                <Chip
                  icon={<DiamondIcon />}
                  label={
                    fineBalance > 0
                      ? `Fine: ${finePaid.toFixed(3)}g / ${fineRequired.toFixed(3)}g (Bal: ${fineBalance.toFixed(3)}g)`
                      : `Fine: ${finePaid.toFixed(3)}g / ${fineRequired.toFixed(3)}g`
                  }
                  sx={{
                    backgroundColor:
                      fineBalance > 0 ? alpha('#ef4444', 0.15) : alpha('#8b5cf6', 0.15),
                    color: fineBalance > 0 ? '#ef4444' : '#8b5cf6',
                    fontWeight: 700,
                    fontSize: '0.85rem',
                    border: `1px solid ${alpha(fineBalance > 0 ? '#ef4444' : '#8b5cf6', 0.3)}`,
                    backdropFilter: 'blur(10px)',
                  }}
                />
              )}
            </Box>
          </Box>

          <IconButton
            onClick={onClose}
            sx={{
              backgroundColor: isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)',
              color: isDark ? '#94a3b8' : '#64748b',
              width: 48,
              height: 48,
              borderRadius: '12px',
              '&:hover': {
                backgroundColor: isDark ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.1)',
                transform: 'rotate(90deg)',
                transition: 'transform 0.3s ease',
              },
              transition: 'all 0.2s ease',
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </Box>

      {/* ==================== CONTENT SECTION ==================== */}
      <Box sx={{ p: 4, overflow: 'auto', maxHeight: '75vh' }}>
        <Grid container spacing={3}>
          {/* ============ LEFT COLUMN — ITEMS TABLE ============ */}
          <Grid item xs={12} lg={7}>
            <Paper
              sx={{
                p: 3,
                borderRadius: '20px',
                background: isDark
                  ? 'linear-gradient(135deg, rgba(30, 41, 59, 0.8) 0%, rgba(15, 23, 42, 0.8) 100%)'
                  : 'linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(248, 250, 252, 0.9) 100%)',
                backdropFilter: 'blur(20px)',
                border: `1px solid ${alpha(isDark ? '#475569' : '#e2e8f0', isDark ? 0.2 : 0.3)}`,
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
                height: '100%',
                overflow: 'hidden',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  mb: 3,
                }}
              >
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 800,
                    color: isDark ? '#f1f5f9' : '#1e293b',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                  }}
                >
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: '14px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
                      boxShadow: '0 8px 25px rgba(16, 185, 129, 0.3)',
                    }}
                  >
                    <InventoryIcon sx={{ fontSize: 24, color: 'white' }} />
                  </Box>
                  Items Purchased ({transaction.items?.length || 0})
                </Typography>
              </Box>

              <Box
                sx={{
                  borderRadius: '16px',
                  overflow: 'hidden',
                  border: `1px solid ${alpha(isDark ? '#475569' : '#e2e8f0', isDark ? 0.2 : 0.3)}`,
                  background: isDark ? 'rgba(15, 23, 42, 0.5)' : 'rgba(255, 255, 255, 0.7)',
                  backdropFilter: 'blur(10px)',
                }}
              >
                <TableContainer sx={{ maxHeight: 400 }}>
                  <Table stickyHeader size="small">
                    <TableHead>
                      <TableRow>
                        {['#', 'Item Name', 'Serial / QR', 'Metal', 'Purity', 'Weight (g)', 'Labour/g', 'Amount'].map((header, idx) => (
                          <TableCell
                            key={header}
                            align={idx >= 5 ? 'right' : 'left'}
                            sx={{
                              backgroundColor: isDark
                                ? 'rgba(30, 41, 59, 0.9)'
                                : 'rgba(248, 250, 252, 0.95)',
                              color: isDark ? '#94a3b8' : '#64748b',
                              fontWeight: 700,
                              fontSize: '0.8rem',
                              borderBottom: `2px solid ${alpha(isDark ? '#475569' : '#e2e8f0', isDark ? 0.3 : 0.4)}`,
                              py: 1.5,
                            }}
                          >
                            {header}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {transaction.items?.map((item, index) => {
                        // Determine display values with fallbacks
                        const displaySerialOrQR = item.serialNo || item.qr || '-';
                        const displayMetal = item.metal || '-';
                        const displayPurity =
                          item.melting || item.ct || item.purity || item.wastage || '-';
                        const displayWeight = item.weight ?? '-';
                        const displayLabour =
                          item.labour ?? item.makingCharges ?? 0;
                        const displayAmount =
                          item.price ?? item.total ?? 0;

                        return (
                          <TableRow
                            key={index}
                            hover
                            sx={{
                              '&:hover': {
                                backgroundColor: isDark
                                  ? 'rgba(255, 255, 255, 0.05)'
                                  : 'rgba(0, 0, 0, 0.02)',
                                transition: 'all 0.2s ease',
                              },
                              transition: 'all 0.2s ease',
                            }}
                          >
                            <TableCell sx={{ py: 1.5 }}>
                              <Box
                                sx={{
                                  width: 28,
                                  height: 28,
                                  borderRadius: '8px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  background: alpha('#3b82f6', 0.1),
                                  color: '#3b82f6',
                                  fontWeight: 700,
                                  fontSize: '0.875rem',
                                }}
                              >
                                {item.itemNumber || index + 1}
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Typography
                                variant="body2"
                                sx={{
                                  fontWeight: 600,
                                  color: isDark ? '#f1f5f9' : '#1e293b',
                                }}
                              >
                                {item.name || '-'}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography
                                variant="caption"
                                sx={{
                                  color: isDark ? '#94a3b8' : '#64748b',
                                  fontFamily: 'monospace',
                                  fontSize: '0.7rem',
                                  wordBreak: 'break-all',
                                }}
                              >
                                {displaySerialOrQR}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography
                                variant="body2"
                                sx={{
                                  fontWeight: 500,
                                  color: isDark ? '#e2e8f0' : '#334155',
                                }}
                              >
                                {displayMetal}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography
                                variant="body2"
                                sx={{
                                  fontWeight: 500,
                                  color: isDark ? '#e2e8f0' : '#334155',
                                }}
                              >
                                {displayPurity}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              <Typography
                                variant="body2"
                                sx={{ fontWeight: 600, color: isDark ? '#f1f5f9' : '#1e293b' }}
                              >
                                {displayWeight}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              <Box
                                sx={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: 0.5,
                                  backgroundColor: alpha('#f59e0b', 0.1),
                                  padding: '2px 8px',
                                  borderRadius: '6px',
                                  border: `1px solid ${alpha('#f59e0b', 0.2)}`,
                                }}
                              >
                                <Typography variant="body2" sx={{ fontWeight: 600, color: '#f59e0b' }}>
                                  ₹{displayLabour}
                                </Typography>
                              </Box>
                            </TableCell>
                            <TableCell align="right">
                              <Box
                                sx={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: 0.5,
                                  backgroundColor: alpha('#10b981', 0.1),
                                  padding: '2px 8px',
                                  borderRadius: '6px',
                                  border: `1px solid ${alpha('#10b981', 0.2)}`,
                                }}
                              >
                                <Typography variant="body2" sx={{ fontWeight: 700, color: '#10b981' }}>
                                  ₹{Number(displayAmount).toLocaleString('en-IN')}
                                </Typography>
                              </Box>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            </Paper>
          </Grid>

          {/* ============ RIGHT COLUMN — FINANCIAL SUMMARY ============ */}
          <Grid item xs={12} lg={5}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {/* ===== PAYMENT SUMMARY CARD ===== */}
              <Paper
                sx={{
                  p: 3,
                  borderRadius: '20px',
                  background: isDark
                    ? 'linear-gradient(135deg, rgba(30, 41, 59, 0.8) 0%, rgba(15, 23, 42, 0.8) 100%)'
                    : 'linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(248, 250, 252, 0.9) 100%)',
                  backdropFilter: 'blur(20px)',
                  border: `1px solid ${alpha(isDark ? '#475569' : '#e2e8f0', isDark ? 0.2 : 0.3)}`,
                  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    mb: 3,
                    fontWeight: 800,
                    color: isDark ? '#f1f5f9' : '#1e293b',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                  }}
                >
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: '12px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      background: 'linear-gradient(135deg, #3B82F6 0%, #1D4ED8 100%)',
                      boxShadow: '0 4px 20px rgba(59, 130, 246, 0.3)',
                    }}
                  >
                    <PaymentIcon sx={{ fontSize: 20, color: 'white' }} />
                  </Box>
                  Payment Summary
                </Typography>

                <Stack spacing={2}>
                  {/* Regular Payments (cash, online, cheque, other) */}
                  {['cash', 'online', 'cheque', 'other'].map((key) => {
                    const value = transaction.paymentDetails?.[key] || 0;
                    if (value === 0) return null;

                    return (
                      <Box
                        key={key}
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          py: 1.5,
                          px: 2,
                          borderRadius: '10px',
                          background: isDark
                            ? 'rgba(255, 255, 255, 0.05)'
                            : 'rgba(0, 0, 0, 0.02)',
                          border: `1px solid ${alpha(isDark ? '#475569' : '#e2e8f0', isDark ? 0.1 : 0.2)}`,
                          transition: 'all 0.2s ease',
                          '&:hover': {
                            background: isDark
                              ? 'rgba(255, 255, 255, 0.08)'
                              : 'rgba(0, 0, 0, 0.04)',
                            transform: 'translateX(4px)',
                          },
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <Box
                            sx={{
                              width: 32,
                              height: 32,
                              borderRadius: '8px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              background: alpha('#3b82f6', 0.1),
                              border: `1px solid ${alpha('#3b82f6', 0.2)}`,
                            }}
                          >
                            {key === 'cash' && <LocalAtmIcon sx={{ fontSize: 16, color: '#3b82f6' }} />}
                            {key === 'cheque' && <AccountBalanceIcon sx={{ fontSize: 16, color: '#3b82f6' }} />}
                            {key === 'online' && <PaidIcon sx={{ fontSize: 16, color: '#3b82f6' }} />}
                            {key === 'other' && <PaymentIcon sx={{ fontSize: 16, color: '#3b82f6' }} />}
                          </Box>
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 600,
                              color: isDark ? '#f1f5f9' : '#1e293b',
                              textTransform: 'capitalize',
                            }}
                          >
                            {key}
                          </Typography>
                        </Box>
                        <Typography
                          variant="body1"
                          sx={{
                            fontWeight: 700,
                            color: '#10b981',
                            fontSize: '1rem',
                          }}
                        >
                          ₹{Number(value).toLocaleString('en-IN')}
                        </Typography>
                      </Box>
                    );
                  })}

                  {/* Discount */}
                  {transaction.paymentDetails?.discount > 0 && (
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        py: 1.5,
                        px: 2,
                        borderRadius: '10px',
                        background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(5, 150, 105, 0.05) 100%)',
                        border: `1px solid ${alpha('#10b981', 0.2)}`,
                        mt: 1,
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box
                          sx={{
                            width: 32,
                            height: 32,
                            borderRadius: '8px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            background: alpha('#10b981', 0.15),
                            border: `1px solid ${alpha('#10b981', 0.3)}`,
                          }}
                        >
                          <DiscountIcon sx={{ fontSize: 16, color: '#10b981' }} />
                        </Box>
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 600, color: '#10b981' }}
                        >
                          Discount
                        </Typography>
                      </Box>
                      <Typography
                        variant="body1"
                        sx={{
                          fontWeight: 700,
                          color: '#10b981',
                          fontSize: '1rem',
                        }}
                      >
                        -₹{Number(transaction.paymentDetails.discount).toLocaleString('en-IN')}
                      </Typography>
                    </Box>
                  )}

                  {/* ===== FINE PAYMENT SECTION ===== */}
                  {hasFine && (
                    <Box
                      sx={{
                        mt: 2,
                        pt: 2,
                        borderTop: `2px dashed ${alpha('#8b5cf6', 0.3)}`,
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1.5,
                          mb: 2,
                          p: 1.5,
                          borderRadius: '10px',
                          background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.12) 0%, rgba(124, 58, 237, 0.06) 100%)',
                          border: `1px solid ${alpha('#8b5cf6', 0.25)}`,
                        }}
                      >
                        <DiamondIcon sx={{ fontSize: 20, color: '#8b5cf6' }} />
                        <Typography
                          variant="subtitle1"
                          sx={{ fontWeight: 700, color: '#8b5cf6', flex: 1 }}
                        >
                          Fine Payment
                        </Typography>
                        {fineBalance > 0 && (
                          <Chip
                            label={`Balance: ${fineBalance.toFixed(3)}g`}
                            size="small"
                            sx={{
                              backgroundColor: alpha('#ef4444', 0.15),
                              color: '#ef4444',
                              fontWeight: 700,
                              fontSize: '0.7rem',
                              border: `1px solid ${alpha('#ef4444', 0.3)}`,
                            }}
                          />
                        )}
                      </Box>

                      <Stack spacing={1.5}>
                        {/* Required */}
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            py: 1,
                            px: 2,
                            borderRadius: '8px',
                            background: isDark
                              ? 'rgba(139, 92, 246, 0.08)'
                              : 'rgba(139, 92, 246, 0.05)',
                            border: `1px solid ${alpha('#8b5cf6', 0.15)}`,
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{ color: isDark ? '#94a3b8' : '#64748b', fontWeight: 500 }}
                          >
                            Fine Required
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: 700, color: isDark ? '#f1f5f9' : '#1e293b' }}
                          >
                            {fineRequired.toFixed(3)} g
                          </Typography>
                        </Box>

                        {/* Paid */}
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            py: 1,
                            px: 2,
                            borderRadius: '8px',
                            background: isDark
                              ? 'rgba(16, 185, 129, 0.08)'
                              : 'rgba(16, 185, 129, 0.05)',
                            border: `1px solid ${alpha('#10b981', 0.2)}`,
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{ color: isDark ? '#94a3b8' : '#64748b', fontWeight: 500 }}
                          >
                            Fine Paid
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: 700, color: '#10b981' }}
                          >
                            {finePaid.toFixed(3)} g
                          </Typography>
                        </Box>

                        {/* Balance */}
                        {fineBalance > 0 && (
                          <Box
                            sx={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              py: 1,
                              px: 2,
                              borderRadius: '8px',
                              background: isDark
                                ? 'rgba(239, 68, 68, 0.08)'
                                : 'rgba(239, 68, 68, 0.05)',
                              border: `1px solid ${alpha('#ef4444', 0.25)}`,
                            }}
                          >
                            <Typography
                              variant="body2"
                              sx={{ color: isDark ? '#94a3b8' : '#64748b', fontWeight: 500 }}
                            >
                              Fine Balance
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 700, color: '#ef4444' }}
                            >
                              {fineBalance.toFixed(3)} g
                            </Typography>
                          </Box>
                        )}

                        {/* Individual Metal Entries */}
                        {fineEntries.length > 0 && (
                          <Box
                            sx={{
                              mt: 1,
                              p: 1.5,
                              borderRadius: '10px',
                              background: isDark
                                ? 'rgba(255, 255, 255, 0.03)'
                                : 'rgba(0, 0, 0, 0.02)',
                              border: `1px dashed ${alpha('#8b5cf6', 0.3)}`,
                            }}
                          >
                            <Typography
                              variant="caption"
                              sx={{
                                color: isDark ? '#94a3b8' : '#64748b',
                                fontWeight: 600,
                                display: 'block',
                                mb: 1,
                                textTransform: 'uppercase',
                                letterSpacing: 0.5,
                                fontSize: '0.65rem',
                              }}
                            >
                              Metal Pieces Received ({fineEntries.length})
                            </Typography>
                            <Stack spacing={0.75}>
                              {fineEntries.map((entry, idx) => {
                                const entryFine =
                                  entry.fineWeight ??
                                  ((parseFloat(entry.weight) || 0) *
                                    (parseFloat(entry.purity) || 0)) /
                                  100;

                                return (
                                  <Box
                                    key={idx}
                                    sx={{
                                      display: 'flex',
                                      justifyContent: 'space-between',
                                      alignItems: 'center',
                                      py: 0.75,
                                      px: 1.5,
                                      borderRadius: '6px',
                                      background: isDark
                                        ? 'rgba(139, 92, 246, 0.05)'
                                        : 'rgba(139, 92, 246, 0.03)',
                                      border: `1px solid ${alpha('#8b5cf6', 0.1)}`,
                                    }}
                                  >
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                      <Box
                                        sx={{
                                          width: 22,
                                          height: 22,
                                          borderRadius: '6px',
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                          background: alpha('#8b5cf6', 0.15),
                                          color: '#8b5cf6',
                                          fontWeight: 700,
                                          fontSize: '0.65rem',
                                        }}
                                      >
                                        {idx + 1}
                                      </Box>
                                      <Box>
                                        <Typography
                                          variant="caption"
                                          sx={{
                                            fontWeight: 600,
                                            color: isDark ? '#e2e8f0' : '#1e293b',
                                            textTransform: 'capitalize',
                                            display: 'block',
                                            fontSize: '0.75rem',
                                          }}
                                        >
                                          {entry.metal || 'Silver'}
                                        </Typography>
                                        <Typography
                                          variant="caption"
                                          sx={{
                                            color: isDark ? '#94a3b8' : '#64748b',
                                            fontSize: '0.65rem',
                                          }}
                                        >
                                          {parseFloat(entry.weight || 0).toFixed(3)}g @{' '}
                                          {parseFloat(entry.purity || 0).toFixed(1)}%
                                        </Typography>
                                      </Box>
                                    </Box>
                                    <Typography
                                      variant="caption"
                                      sx={{
                                        fontWeight: 700,
                                        color: '#8b5cf6',
                                        fontSize: '0.8rem',
                                      }}
                                    >
                                      = {entryFine.toFixed(3)}g
                                    </Typography>
                                  </Box>
                                );
                              })}
                            </Stack>
                          </Box>
                        )}
                      </Stack>
                    </Box>
                  )}

                  {/* Advance Payment Section */}
                  {transaction.paymentDetails?.advancePayment?.enabled && (
                    <Box
                      sx={{
                        mt: 2,
                        pt: 2,
                        borderTop: `1px solid ${alpha('#f59e0b', 0.2)}`,
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1.5,
                          mb: 2,
                          p: 1.5,
                          borderRadius: '10px',
                          background: 'linear-gradient(135deg, rgba(245, 158, 11, 0.1) 0%, rgba(217, 119, 6, 0.05) 100%)',
                          border: `1px solid ${alpha('#f59e0b', 0.2)}`,
                        }}
                      >
                        <TrendingUpIcon sx={{ fontSize: 20, color: '#f59e0b' }} />
                        <Typography
                          variant="subtitle1"
                          sx={{ fontWeight: 700, color: '#f59e0b' }}
                        >
                          Advance Payment
                        </Typography>
                        <Chip
                          label={`₹${transaction.paymentDetails.advancePayment.total?.toLocaleString('en-IN') || 0}`}
                          size="small"
                          sx={{
                            backgroundColor: alpha('#f59e0b', 0.2),
                            color: '#f59e0b',
                            fontWeight: 700,
                            border: `1px solid ${alpha('#f59e0b', 0.3)}`,
                          }}
                        />
                      </Box>

                      {transaction.paymentDetails.advancePayment.breakup && (
                        <Stack spacing={1.5}>
                          {Object.entries(transaction.paymentDetails.advancePayment.breakup).map(([breakupKey, breakupValue]) => {
                            if (breakupKey === 'customOptions' || !breakupValue) return null;

                            return (
                              <Box
                                key={breakupKey}
                                sx={{
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  alignItems: 'center',
                                  py: 1,
                                  px: 2,
                                  borderRadius: '8px',
                                  background: isDark
                                    ? 'rgba(255, 255, 255, 0.03)'
                                    : 'rgba(0, 0, 0, 0.01)',
                                  border: `1px solid ${alpha('#f59e0b', 0.1)}`,
                                }}
                              >
                                <Typography
                                  variant="caption"
                                  sx={{
                                    color: isDark ? '#94a3b8' : '#64748b',
                                    textTransform: 'capitalize',
                                  }}
                                >
                                  {breakupKey.replace(/([A-Z])/g, ' $1').trim()}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  sx={{ fontWeight: 600, color: '#f59e0b' }}
                                >
                                  ₹{Number(breakupValue).toLocaleString('en-IN')}
                                </Typography>
                              </Box>
                            );
                          })}
                        </Stack>
                      )}
                    </Box>
                  )}

                  {/* Total Paid */}
                  {transaction.paymentDetails?.totalPaymentDone > 0 && (
                    <Box
                      sx={{
                        mt: 2,
                        pt: 2,
                        borderTop: `1px solid ${alpha('#10b981', 0.3)}`,
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          py: 1.5,
                          px: 2,
                          borderRadius: '10px',
                          background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.15) 0%, rgba(5, 150, 105, 0.1) 100%)',
                          border: `1px solid ${alpha('#10b981', 0.3)}`,
                        }}
                      >
                        <Typography
                          variant="body1"
                          sx={{ fontWeight: 700, color: '#10b981' }}
                        >
                          Total Paid
                        </Typography>
                        <Typography
                          variant="h6"
                          sx={{
                            fontWeight: 800,
                            color: '#10b981',
                          }}
                        >
                          ₹{Number(transaction.paymentDetails.totalPaymentDone).toLocaleString('en-IN')}
                        </Typography>
                      </Box>
                    </Box>
                  )}
                </Stack>
              </Paper>

              {/* ===== TOTALS SUMMARY CARD ===== */}
              <Paper
                sx={{
                  p: 3,
                  borderRadius: '20px',
                  background: isDark
                    ? 'linear-gradient(135deg, rgba(30, 41, 59, 0.8) 0%, rgba(15, 23, 42, 0.8) 100%)'
                    : 'linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(248, 250, 252, 0.9) 100%)',
                  backdropFilter: 'blur(20px)',
                  border: `1px solid ${alpha(isDark ? '#475569' : '#e2e8f0', isDark ? 0.2 : 0.3)}`,
                  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    mb: 3,
                    fontWeight: 800,
                    color: isDark ? '#f1f5f9' : '#1e293b',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                  }}
                >
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: '12px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      background: 'linear-gradient(135deg, #8B5CF6 0%, #7C3AED 100%)',
                      boxShadow: '0 4px 20px rgba(139, 92, 246, 0.3)',
                    }}
                  >
                    <TrendingUpIcon sx={{ fontSize: 20, color: 'white' }} />
                  </Box>
                  Financial Summary
                </Typography>

                <Stack spacing={2}>
                  {/* Subtotal */}
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      py: 1.5,
                    }}
                  >
                    <Typography variant="body2" sx={{ color: isDark ? '#94a3b8' : '#64748b' }}>
                      Subtotal
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      ₹{Number(transaction.calculatedTotals?.subtotal || 0).toLocaleString('en-IN')}
                    </Typography>
                  </Box>

                  {/* Discount */}
                  {transaction.paymentDetails?.discount > 0 && (
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        py: 1.5,
                      }}
                    >
                      <Typography variant="body2" sx={{ color: isDark ? '#94a3b8' : '#64748b' }}>
                        Discount
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 600, color: '#10b981' }}>
                        -₹{Number(transaction.paymentDetails.discount).toLocaleString('en-IN')}
                      </Typography>
                    </Box>
                  )}

                  {/* GST */}
                  {transaction.transactionDetails?.gstApplicable && (
                    <>
                      {transaction.summary?.igst > 0 ? (
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            py: 1.5,
                          }}
                        >
                          <Typography variant="body2" sx={{ color: isDark ? '#94a3b8' : '#64748b' }}>
                            IGST (3%)
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 600, color: '#ef4444' }}>
                            ₹{Number(transaction.summary?.igst || 0).toLocaleString('en-IN')}
                          </Typography>
                        </Box>
                      ) : (
                        <>
                          <Box
                            sx={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              py: 1.5,
                            }}
                          >
                            <Typography variant="body2" sx={{ color: isDark ? '#94a3b8' : '#64748b' }}>
                              CGST (1.5%)
                            </Typography>
                            <Typography variant="body1" sx={{ fontWeight: 600, color: '#ef4444' }}>
                              ₹{Number(transaction.summary?.cgst || 0).toLocaleString('en-IN')}
                            </Typography>
                          </Box>
                          <Box
                            sx={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              py: 1.5,
                            }}
                          >
                            <Typography variant="body2" sx={{ color: isDark ? '#94a3b8' : '#64748b' }}>
                              SGST (1.5%)
                            </Typography>
                            <Typography variant="body1" sx={{ fontWeight: 600, color: '#ef4444' }}>
                              ₹{Number(transaction.summary?.sgst || 0).toLocaleString('en-IN')}
                            </Typography>
                          </Box>
                        </>
                      )}
                    </>
                  )}

                  <Divider sx={{ borderColor: isDark ? '#475569' : '#e2e8f0' }} />

                  {/* Grand Total */}
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      py: 2,
                      px: 2,
                      borderRadius: '12px',
                      background: 'linear-gradient(135deg, rgba(37, 99, 235, 0.1) 0%, rgba(29, 78, 216, 0.05) 100%)',
                      border: `1px solid ${alpha('#2563eb', 0.2)}`,
                      mt: 1,
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 800,
                        color: isDark ? '#f1f5f9' : '#1e293b',
                      }}
                    >
                      Grand Total
                    </Typography>
                    <Typography
                      variant="h4"
                      sx={{
                        fontWeight: 900,
                        background: 'linear-gradient(135deg, #2563EB 0%, #1D4ED8 100%)',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        backgroundClip: 'text',
                      }}
                    >
                      ₹{Number(transaction.summary?.grandTotal || 0).toLocaleString('en-IN')}
                    </Typography>
                  </Box>

                  {/* Total Net Weight */}
                  {totalNetWeight > 0 && (
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        py: 1.5,
                        px: 2,
                        borderRadius: '10px',
                        background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.1) 0%, rgba(124, 58, 237, 0.05) 100%)',
                        border: `1px solid ${alpha('#8b5cf6', 0.2)}`,
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <ScaleIcon sx={{ fontSize: 18, color: '#8b5cf6' }} />
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 600, color: '#8b5cf6' }}
                        >
                          Total Net Weight
                        </Typography>
                      </Box>
                      <Typography
                        variant="h6"
                        sx={{ fontWeight: 800, color: '#8b5cf6' }}
                      >
                        {totalNetWeight.toFixed(3)} g
                      </Typography>
                    </Box>
                  )}

                  {/* Payment Status */}
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      py: 1.5,
                      px: 2,
                      borderRadius: '10px',
                      background:
                        transaction.summary?.pendingAmount > 0 || fineBalance > 0
                          ? 'linear-gradient(135deg, rgba(239, 68, 68, 0.1) 0%, rgba(220, 38, 38, 0.05) 100%)'
                          : 'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(5, 150, 105, 0.05) 100%)',
                      border: `1px solid ${alpha(
                        transaction.summary?.pendingAmount > 0 || fineBalance > 0
                          ? '#ef4444'
                          : '#10b981',
                        0.2
                      )}`,
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {transaction.summary?.pendingAmount > 0 || fineBalance > 0 ? (
                        <ErrorOutlineIcon sx={{ fontSize: 20, color: '#ef4444' }} />
                      ) : (
                        <CheckCircleOutlineIcon sx={{ fontSize: 20, color: '#10b981' }} />
                      )}
                      <Typography
                        variant="body1"
                        sx={{
                          fontWeight: 700,
                          color:
                            transaction.summary?.pendingAmount > 0 || fineBalance > 0
                              ? '#ef4444'
                              : '#10b981',
                        }}
                      >
                        {transaction.summary?.pendingAmount > 0 || fineBalance > 0
                          ? 'Pending'
                          : 'Payment Status'}
                      </Typography>
                    </Box>
                    <Box sx={{ textAlign: 'right' }}>
                      {transaction.summary?.pendingAmount > 0 && (
                        <Typography
                          variant="h6"
                          sx={{ fontWeight: 800, color: '#ef4444', lineHeight: 1.2 }}
                        >
                          ₹{Number(transaction.summary.pendingAmount).toLocaleString('en-IN')}
                        </Typography>
                      )}
                      {fineBalance > 0 && (
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 700, color: '#8b5cf6', fontSize: '0.85rem' }}
                        >
                          {fineBalance.toFixed(3)} g Fine
                        </Typography>
                      )}
                      {transaction.summary?.pendingAmount === 0 && fineBalance === 0 && (
                        <Typography
                          variant="h6"
                          sx={{ fontWeight: 800, color: '#10b981' }}
                        >
                          Fully Paid
                        </Typography>
                      )}
                    </Box>
                  </Box>

                  {/* Cash Payment Progress */}
                  {transaction.summary?.pendingAmount > 0 && (
                    <Box sx={{ mt: 2 }}>
                      <Typography
                        variant="caption"
                        sx={{
                          color: isDark ? '#94a3b8' : '#64748b',
                          mb: 1,
                          display: 'block',
                        }}
                      >
                        Cash Payment Progress
                      </Typography>
                      <LinearProgress
                        variant="determinate"
                        value={
                          ((transaction.summary?.grandTotal - transaction.summary?.pendingAmount) /
                            transaction.summary?.grandTotal) *
                          100
                        }
                        sx={{
                          height: 8,
                          borderRadius: 4,
                          backgroundColor: isDark ? '#374151' : '#e5e7eb',
                          '& .MuiLinearProgress-bar': {
                            background: 'linear-gradient(90deg, #10b981, #34d399)',
                            borderRadius: 4,
                          },
                        }}
                      />
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                        <Typography variant="caption" sx={{ color: '#10b981' }}>
                          {(
                            ((transaction.summary?.grandTotal - transaction.summary?.pendingAmount) /
                              transaction.summary?.grandTotal) *
                            100
                          ).toFixed(1)}% Paid
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#ef4444' }}>
                          {(
                            (transaction.summary?.pendingAmount /
                              transaction.summary?.grandTotal) *
                            100
                          ).toFixed(1)}% Pending
                        </Typography>
                      </Box>
                    </Box>
                  )}

                  {/* Fine Payment Progress */}
                  {fineRequired > 0 && (
                    <Box sx={{ mt: 2 }}>
                      <Typography
                        variant="caption"
                        sx={{
                          color: isDark ? '#94a3b8' : '#64748b',
                          mb: 1,
                          display: 'block',
                        }}
                      >
                        Fine Payment Progress
                      </Typography>
                      <LinearProgress
                        variant="determinate"
                        value={Math.min(100, (finePaid / fineRequired) * 100)}
                        sx={{
                          height: 8,
                          borderRadius: 4,
                          backgroundColor: isDark ? '#374151' : '#e5e7eb',
                          '& .MuiLinearProgress-bar': {
                            background: 'linear-gradient(90deg, #8b5cf6, #a78bfa)',
                            borderRadius: 4,
                          },
                        }}
                      />
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                        <Typography variant="caption" sx={{ color: '#8b5cf6' }}>
                          {finePaid.toFixed(3)}g / {fineRequired.toFixed(3)}g
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#8b5cf6' }}>
                          {Math.min(100, (finePaid / fineRequired) * 100).toFixed(1)}% Complete
                        </Typography>
                      </Box>
                    </Box>
                  )}
                </Stack>
              </Paper>
            </Box>
          </Grid>
        </Grid>
      </Box>
    </Dialog>
  );
};

// ================================================================
// MAIN COMPONENT
// ================================================================
const TransactionHistory = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const {
    customerTransactions,
    customers,
    loading: contextLoading,
    refresh,
    removeCustomerTransactionAndUpdateStock,
    stock
  } = useContext(DataContext);

  // Refs
  const gridRef = useRef();

  // States
  const isPageLoading = false;
  const isSearching = false;
  const isFiltering = false;
  const [isExporting, setIsExporting] = useState(false);
  const [silverItemsArray, setSilverItemsArray] = useState([]);
  const [openModal, setOpenModal] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [searchInput, setSearchInput] = useState('');
  const [customerFilter, setCustomerFilter] = useState('all');
  const [dates, setDates] = useState({
    startDate: null,
    endDate: null
  });
  const [alertMessage, setAlertMessage] = useState('');
  const [alertType, setAlertType] = useState('success');
  const [filteredTransactions, setFilteredTransactions] = useState([]);
  const [isSharing, setIsSharing] = useState(false);

  // Cancel modal states
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [selectedCancelTransaction, setSelectedCancelTransaction] = useState(null);
  const [refundAmounts, setRefundAmounts] = useState({
    cash: 0,
    bank: 0,
    total: 0
  });
  const [cancellingItems, setCancellingItems] = useState([]);
  const [isCancelling, setIsCancelling] = useState(false);

  // Debounced search
  const searchTerm = useDebounce(searchInput, 300);

  // Initialize default dates (current financial year)
  useEffect(() => {
    const today = new Date();
    const currentYear = today.getFullYear();
    const currentMonth = today.getMonth();

    let startDate, endDate;

    if (currentMonth >= 3) {
      startDate = new Date(currentYear, 3, 1);
      endDate = today;
    } else {
      startDate = new Date(currentYear - 1, 3, 1);
      endDate = today;
    }

    setDates({ startDate, endDate });
  }, []);

  // Apply all filters
  const applyFilters = useCallback(() => {
    if (!customerTransactions || customerTransactions.length === 0) {
      setFilteredTransactions([]);
      return;
    }

    let filtered = [...customerTransactions];

    if (customerFilter !== 'all') {
      filtered = filtered.filter(transaction =>
        transaction.customer === customerFilter
      );
    }

    if (dates.startDate && dates.endDate) {
      const startDateStr = format(dates.startDate, 'dd/MM/yyyy');
      const endDateStr = format(dates.endDate, 'dd/MM/yyyy');
      filtered = filterByDateRange(filtered, startDateStr, endDateStr);
    }

    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(transaction => {
        const customer = customers.find(c => c.id === transaction.customer);
        const customerNameMatch = customer?.name?.toLowerCase().includes(term);
        const billNoMatch = transaction.transactionDetails?.billNo?.toString().includes(term);
        const itemsMatch = transaction.items?.some(item =>
          item.name?.toLowerCase().includes(term) ||
          item.huid?.toLowerCase().includes(term)
        );
        const dateMatch = transaction.date?.toLowerCase().includes(term);
        return customerNameMatch || billNoMatch || itemsMatch || dateMatch;
      });
    }

    filtered.sort((a, b) => {
      if (a.createdAt && b.createdAt) {
        return new Date(b.createdAt) - new Date(a.createdAt);
      }
      if (a.transactionTime && b.transactionTime) {
        const dateTimeA = new Date(`${a.date} ${a.transactionTime}`);
        const dateTimeB = new Date(`${b.date} ${b.transactionTime}`);
        return dateTimeB - dateTimeA;
      }
      const parseDate = (dateStr) => {
        try {
          return parse(dateStr, 'dd/MM/yyyy', new Date());
        } catch {
          return new Date(0);
        }
      };
      const dateA = parseDate(a.date);
      const dateB = parseDate(b.date);
      return dateB - dateA;
    });

    setFilteredTransactions(filtered);
  }, [customerTransactions, customerFilter, dates.startDate, dates.endDate, searchTerm, customers]);

  useEffect(() => {
    applyFilters();
  }, [customerTransactions, customerFilter, dates.startDate, dates.endDate, searchTerm, applyFilters]);

  function filterByDateRange(records, startDate, endDate) {
    if (!records || records.length === 0) return [];
    if (!startDate || !endDate) return records;

    const parseFilterDate = (dateStr) => {
      if (!dateStr) return null;
      if (dateStr instanceof Date) return dateStr;

      const parts = dateStr.split('/');
      if (parts.length === 3) {
        const day = parseInt(parts[0], 10);
        const month = parseInt(parts[1], 10) - 1;
        const year = parseInt(parts[2], 10);
        if (isNaN(day) || isNaN(month) || isNaN(year)) return null;
        const date = new Date(year, month, day);
        if (isNaN(date.getTime()) ||
          date.getDate() !== day ||
          date.getMonth() !== month ||
          date.getFullYear() !== year) {
          return null;
        }
        return date;
      }
      const isoDate = new Date(dateStr);
      return isNaN(isoDate.getTime()) ? null : isoDate;
    };

    const parseRecordDate = (dateStr) => {
      if (!dateStr) return null;
      if (dateStr instanceof Date) return dateStr;

      if (typeof dateStr === 'string') {
        const parts = dateStr.split('/');
        if (parts.length === 3) {
          const day = parseInt(parts[0], 10);
          const month = parseInt(parts[1], 10) - 1;
          const year = parseInt(parts[2], 10);
          if (!isNaN(day) && !isNaN(month) && !isNaN(year)) {
            const date = new Date(year, month, day);
            if (!isNaN(date.getTime()) &&
              date.getDate() === day &&
              date.getMonth() === month &&
              date.getFullYear() === year) {
              return date;
            }
          }
        }
        if (dateStr.includes('-')) {
          const isoParts = dateStr.split('-');
          if (isoParts.length === 3) {
            const year = parseInt(isoParts[0], 10);
            const month = parseInt(isoParts[1], 10) - 1;
            const day = parseInt(isoParts[2], 10);
            if (!isNaN(day) && !isNaN(month) && !isNaN(year)) {
              const date = new Date(year, month, day);
              if (!isNaN(date.getTime())) return date;
            }
          }
        }
        const isoDate = new Date(dateStr);
        if (!isNaN(isoDate.getTime())) return isoDate;
      }
      return null;
    };

    const start = parseFilterDate(startDate);
    const end = parseFilterDate(endDate);
    if (!start || !end) return records;

    const endOfDay = new Date(end);
    endOfDay.setHours(23, 59, 59, 999);

    return records.filter(record => {
      if (!record.date) return false;
      const recordDate = parseRecordDate(record.date);
      if (!recordDate) return false;
      return recordDate >= start && recordDate <= endOfDay;
    });
  }

  const showAlert = useCallback((message, type) => {
    setAlertMessage(message);
    setAlertType(type);
    setTimeout(() => setAlertMessage(''), 3000);
  }, []);

  // ============ PDF / PRINT HELPERS ============
  const generatePDFBlob = useCallback(async (transaction) => {
    try {
      const billHtml = generateTransactionBillHTML(transaction, customers);
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = billHtml;
      tempDiv.style.width = '210mm';
      tempDiv.style.padding = '0';
      tempDiv.style.margin = '0';
      tempDiv.style.backgroundColor = '#ffffff';
      tempDiv.style.position = 'fixed';
      tempDiv.style.left = '-9999px';
      tempDiv.style.top = '0';
      document.body.appendChild(tempDiv);

      await new Promise(resolve => setTimeout(resolve, 300));

      const canvas = await html2canvas(tempDiv, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff',
      });

      document.body.removeChild(tempDiv);

      const pdf = new jsPDF('p', 'mm', 'a4');
      const imgData = canvas.toDataURL('image/jpeg', 0.95);
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const imgWidth = canvas.width;
      const imgHeight = canvas.height;
      const ratio = imgWidth / imgHeight;

      let finalWidth, finalHeight;
      const margin = 10;
      const maxWidth = pageWidth - 2 * margin;
      const maxHeight = pageHeight - 2 * margin;

      if (imgWidth > imgHeight) {
        finalWidth = maxWidth;
        finalHeight = maxWidth / ratio;
        if (finalHeight > maxHeight) {
          finalHeight = maxHeight;
          finalWidth = maxHeight * ratio;
        }
      } else {
        finalHeight = maxHeight;
        finalWidth = maxHeight * ratio;
        if (finalWidth > maxWidth) {
          finalWidth = maxWidth;
          finalHeight = maxWidth / ratio;
        }
      }

      const x = (pageWidth - finalWidth) / 2;
      const y = (pageHeight - finalHeight) / 2;
      pdf.addImage(imgData, 'JPEG', x, y, finalWidth, finalHeight);

      return pdf.output('blob');
    } catch (error) {
      console.error('Error generating PDF:', error);
      throw error;
    }
  }, [customers]);

  const handleMobileShare = useCallback(async (transaction) => {
    try {
      setIsSharing(true);
      showAlert('Generating PDF for sharing...', 'info');

      const pdfBlob = await generatePDFBlob(transaction);
      const fileName = `Bill_${transaction.transactionDetails?.billNo || 'N/A'}.pdf`;
      const pdfFile = new File([pdfBlob], fileName, { type: 'application/pdf' });

      if (navigator.canShare && navigator.canShare({ files: [pdfFile] })) {
        await navigator.share({
          files: [pdfFile],
          title: `Bill #${transaction.transactionDetails?.billNo}`,
        });
        showAlert('PDF shared successfully!', 'success');
      } else {
        const customer = customers.find(c => c.id === transaction.customer);
        const shareData = {
          title: `Bill #${transaction.transactionDetails?.billNo}`,
          text: `Bill #${transaction.transactionDetails?.billNo}\nCustomer: ${customer?.name || 'N/A'}\nTotal: ₹${transaction.summary?.grandTotal?.toLocaleString('en-IN')}`,
        };
        await navigator.share(shareData);
        showAlert('Bill details shared!', 'success');
      }
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('Share error:', error);
        showAlert('Failed to share PDF. Please try again.', 'error');
      }
    } finally {
      setIsSharing(false);
    }
  }, [customers, showAlert, generatePDFBlob]);

  const handleDesktopPrint = useCallback((transaction) => {
    if (!transaction) return;

    const isGst = transaction.transactionDetails?.gstApplicable;
    const billHtml = generateTransactionBillHTML(transaction, customers);
    const styles = getBillStyles();

    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      alert('Please allow popups to print the bill');
      return;
    }

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Print Bill - ${isGst ? 'GST Invoice' : 'Cash Memo'}</title>
          <meta charset="UTF-8">
          <meta name="viewport" content="width=device-width, initial-scale=1.0">
          <style>
            ${styles}
            @media print {
              body {
                -webkit-print-color-adjust: exact !important;
                color-adjust: exact !important;
                print-color-adjust: exact !important;
              }
              table { page-break-inside: avoid !important; break-inside: avoid !important; }
              tr { page-break-inside: avoid !important; page-break-after: auto !important; break-inside: avoid !important; }
              thead { display: table-header-group !important; }
              tfoot { display: table-footer-group !important; }
            }
          </style>
        </head>
        <body>
          ${billHtml}
          <script>
            window.onload = function() {
              setTimeout(function() {
                window.print();
                window.onafterprint = function() {
                  setTimeout(() => window.close(), 100);
                };
              }, 500);
            };
          </script>
        </body>
      </html>
    `);
    printWindow.document.close();
  }, [customers]);

  const handlePrintBill = useCallback((transaction) => {
    if (!transaction) return;
    if (isMobileDevice() && navigator.share) {
      handleMobileShare(transaction);
      return;
    }
    handleDesktopPrint(transaction);
  }, [handleMobileShare, handleDesktopPrint]);

  // ============ CANCEL BILL LOGIC ============
  const handleRefundChange = (type, value) => {
    const numValue = parseFloat(value) || 0;
    setRefundAmounts(prev => {
      const updated = { ...prev, [type]: numValue };
      updated.total = updated.cash + updated.bank;
      return updated;
    });
  };

  const handleCancelModalClose = () => {
    setCancelModalOpen(false);
    setSelectedCancelTransaction(null);
    setRefundAmounts({ cash: 0, bank: 0, total: 0 });
    setCancellingItems([]);
    setSilverItemsArray([]);
  };

  const handleCancelBill = useCallback((transaction) => {
    const parseJewelryName = (name) => {
      if (!name || typeof name !== 'string') {
        return { type: '', serialNo: '', metal: '', purity: '', originalName: name || '', isValid: false };
      }
      const matchingItems = stock.filter(stockItem =>
        stockItem.qr === name &&
        (stockItem.sold === "Yes" || stockItem.sold === true)
      );
      return {
        type: matchingItems[0]?.name || '',
        serialNo: matchingItems[0]?.serialNo || '',
        metal: matchingItems[0]?.metal || '',
        purity: matchingItems[0]?.melting || '',
        originalName: matchingItems[0]?.name || '',
        isValid: !!matchingItems[0]?.name || !!matchingItems[0]?.serialNo
      };
    };

    const findFirstMissingNumber = (arr, startFrom = 1) => {
      if (arr.length === 0) return startFrom;
      const numberSet = new Set(arr);
      let i = startFrom;
      while (numberSet.has(i)) i++;
      return i;
    };

    const getNextSerialNumber = (itemName, metal, currentStock, assignedInThisBatch = []) => {
      if (!itemName || !metal) return 1;
      const allItemsForType = currentStock.filter(item =>
        item.name === itemName && item.metal === metal
      );
      const allExistingSerialNumbers = allItemsForType
        .map(item => parseInt(item.serialNo) || 0)
        .filter(num => !isNaN(num) && num > 0)
        .sort((a, b) => a - b);
      const unsoldSerialNumbers = allItemsForType
        .filter(item => item.sold === "No" || item.sold === false)
        .map(item => parseInt(item.serialNo) || 0)
        .filter(num => !isNaN(num) && num > 0);
      const currentlyUsedNumbers = [...unsoldSerialNumbers, ...assignedInThisBatch];
      const maxExistingNumber = allExistingSerialNumbers.length > 0
        ? Math.max(...allExistingSerialNumbers) : 0;
      const missingNumber = findFirstMissingNumber(currentlyUsedNumbers, 1);
      if (missingNumber <= maxExistingNumber) return missingNumber;
      return maxExistingNumber + 1;
    };

    const itemsToCancel = [];
    const silverItems = [];
    const assignedSerialNumbersMap = new Map();

    for (let i = 0; i < transaction?.items?.length; i++) {
      const item = transaction.items[i];
      if (!item?.manualItemDetails) {
        const parsedInfo = parseJewelryName(item?.qr);
        if (parsedInfo.isValid) {
          const matchingItems = stock.filter(stockItem =>
            stockItem.qr === item.qr &&
            (stockItem.sold === "Yes" || stockItem.sold === true)
          );
          if (matchingItems.length > 0) {
            const itemToUpdate = matchingItems[0];
            const itemKey = `${itemToUpdate.name}-${itemToUpdate.metal}`;
            if (!assignedSerialNumbersMap.has(itemKey)) {
              assignedSerialNumbersMap.set(itemKey, []);
            }
            const assignedNumbers = assignedSerialNumbersMap.get(itemKey);

            // The serial number the item had before it was sold.
            // Prefer the one saved on the bill, fall back to the stock record.
            const originalSerialNo = item.serialNo ?? itemToUpdate.serialNo ?? parsedInfo.serialNo;

            // A real conflict: a DIFFERENT, unsold item of the same name + metal
            // already uses this serial number (or it is already taken in this batch).
            const hasConflict =
              stock.some(
                (s) =>
                  s.id !== itemToUpdate.id &&
                  s.name === itemToUpdate.name &&
                  s.metal === itemToUpdate.metal &&
                  (s.sold === "No" || s.sold === false) &&
                  String(s.serialNo) === String(originalSerialNo)
              ) || assignedNumbers.map(String).includes(String(originalSerialNo));

            let newSerialNumber = originalSerialNo;
            if (hasConflict) {
              newSerialNumber = getNextSerialNumber(
                itemToUpdate.name,
                itemToUpdate.metal,
                stock,
                assignedNumbers
              );
            }
            assignedNumbers.push(parseInt(newSerialNumber) || newSerialNumber);

            itemsToCancel.push({
              ...itemToUpdate,
              updateData: { sold: "No", soldDescription: "", serialNo: newSerialNumber },
              originalSerialNo,
              hasConflict,
              transactionItem: item
            });
          }
        }
      } else {
        if (item?.manualItemDetails?.metal === "Silver" && item?.manualItemDetails?.silverType) {
          silverItems.push({
            silverType: item.manualItemDetails.silverType,
            weight: parseFloat(item.manualItemDetails.weight) || 0,
            transactionItem: item
          });
        } else if (item?.manualItemDetails?.metal === "Gold") {
          silverItems.push({
            silverType: 'gold',
            weight: parseFloat(item.manualItemDetails.weight) || 0,
            transactionItem: item
          });
        }
      }
    }

    const aggregatedSilverItems = {};
    silverItems.forEach(silverItem => {
      const { silverType, weight, transactionItem } = silverItem;
      if (!aggregatedSilverItems[silverType]) {
        aggregatedSilverItems[silverType] = {
          silverType: silverType,
          totalWeight: 0,
          items: []
        };
      }
      aggregatedSilverItems[silverType].totalWeight += weight;
      aggregatedSilverItems[silverType].items.push({
        ...silverItem,
        transactionItem: transactionItem
      });
    });

    const silverItemsArrayResult = Object.values(aggregatedSilverItems);
    const hasJewelryItems = itemsToCancel.length > 0;
    const hasSilverItems = silverItemsArrayResult.length > 0;

    if (hasJewelryItems || hasSilverItems) {
      setSelectedCancelTransaction(transaction);
      setCancellingItems(itemsToCancel);
      setSilverItemsArray(silverItemsArrayResult);

      const paidAmount = transaction.summary?.paymentDone || transaction.summary?.grandTotal || 0;
      setRefundAmounts({ cash: paidAmount, bank: 0, total: paidAmount });

      setCancelModalOpen(true);
    } else {
      alert(`No sold items found in bill ${transaction.transactionDetails?.billNo} to cancel.`);
    }
  }, [stock]);

  const setCancelModal = useCallback((open, transaction) => {
    setCancelModalOpen(open);
    handleCancelBill(transaction);
  }, [handleCancelBill]);

  const handleConfirmCancellation = async () => {
    setIsCancelling(true);
    try {
      const silverItems = silverItemsArray.map(silverGroup => ({
        silverType: silverGroup.silverType,
        totalWeight: silverGroup.totalWeight,
        itemCount: silverGroup.items.length,
        items: silverGroup.items.map(item => ({
          itemNumber: item.transactionItem?.itemNumber || 0,
          weight: item.weight,
          name: item.transactionItem?.name || 'Silver Item',
          silverType: item.silverType
        }))
      }));

      const cancellationData = {
        transactionId: selectedCancelTransaction.id,
        billNo: selectedCancelTransaction.transactionDetails?.billNo,
        customerId: selectedCancelTransaction.customer,
        refund: {
          cash: refundAmounts.cash,
          bank: refundAmounts.bank,
          total: refundAmounts.total
        },
        items: cancellingItems.map(item => ({
          id: item.id,
          name: item.name,
          huid: item.huid,
          originalSerialNo: item.originalSerialNo,
          newSerialNo: item.updateData.serialNo,
          hasConflict: item.hasConflict,
          updateData: item.updateData
        })),
        silverItems: silverItems,
        cancelledAt: new Date().toISOString()
      };

      await removeCustomerTransactionAndUpdateStock(cancellationData);

      showAlert(`Bill ${selectedCancelTransaction.transactionDetails?.billNo} has been cancelled successfully!`, 'success');
      refresh();
      handleCancelModalClose();
    } catch (error) {
      console.error('Error cancelling bill:', error);
      showAlert('Failed to cancel bill. Please try again.', 'error');
    } finally {
      setIsCancelling(false);
    }
  };

  // ============ ACTIONS RENDERER ============
  const ActionsRenderer = useCallback((props) => {
    const { handleViewTransaction, handlePrintBill } = props.context;
    const isMobile = isMobileDevice();
    const cancelled = isCancelledTransaction(props.data);

    if (cancelled) {
      return (
        <Box sx={{
          display: 'flex',
          gap: 0.5,
          justifyContent: 'center',
          alignItems: 'center',
          height: '100%',
        }}>
          <Tooltip title="View Details">
            <IconButton
              size="small"
              onClick={() => handleViewTransaction(props.data)}
              sx={{
                color: '#6B7280',
                backgroundColor: 'rgba(107, 114, 128, 0.1)',
                backdropFilter: 'blur(10px)',
                borderRadius: '8px',
                padding: '6px',
                '&:hover': { backgroundColor: 'rgba(107, 114, 128, 0.2)' },
                transition: 'all 0.2s ease',
              }}
            >
              <ViewIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Print Cancelled Bill">
            <IconButton
              size="small"
              onClick={() => handlePrintBill(props.data)}
              sx={{
                color: '#6B7280',
                backgroundColor: 'rgba(107, 114, 128, 0.1)',
                backdropFilter: 'blur(10px)',
                borderRadius: '8px',
                padding: '6px',
                '&:hover': { backgroundColor: 'rgba(107, 114, 128, 0.2)' },
                transition: 'all 0.2s ease',
              }}
            >
              <PrintIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Chip
            label="Cancelled"
            size="small"
            color="error"
            variant="outlined"
            sx={{
              fontSize: '0.7rem',
              height: '24px',
              textDecoration: 'line-through',
            }}
          />
        </Box>
      );
    }

    return (
      <Box sx={{
        display: 'flex',
        gap: 0.5,
        justifyContent: 'center',
        alignItems: 'center',
        height: '100%',
      }}>
        <Tooltip title="View Details">
          <IconButton
            size="small"
            onClick={() => handleViewTransaction(props.data)}
            sx={{
              color: '#2563EB',
              backgroundColor: 'rgba(37, 99, 235, 0.1)',
              backdropFilter: 'blur(10px)',
              borderRadius: '8px',
              padding: '6px',
              '&:hover': {
                backgroundColor: 'rgba(37, 99, 235, 0.2)',
                transform: 'translateY(-2px)',
              },
              transition: 'all 0.2s ease',
            }}
          >
            <ViewIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title={isMobile ? "Share Bill" : "Print Bill"}>
          <IconButton
            size="small"
            onClick={() => handlePrintBill(props.data)}
            disabled={isSharing}
            sx={{
              color: isMobile ? '#8B5CF6' : '#10B981',
              backgroundColor: isMobile
                ? 'rgba(139, 92, 246, 0.1)'
                : 'rgba(16, 185, 129, 0.1)',
              backdropFilter: 'blur(10px)',
              borderRadius: '8px',
              padding: '6px',
              '&:hover': {
                backgroundColor: isMobile
                  ? 'rgba(139, 92, 246, 0.2)'
                  : 'rgba(16, 185, 129, 0.2)',
                transform: 'translateY(-2px)',
              },
              '&.Mui-disabled': { opacity: 0.5 },
              transition: 'all 0.2s ease',
            }}
          >
            {isSharing ? (
              <CircularProgress size={16} sx={{ color: isMobile ? '#8B5CF6' : '#10B981' }} />
            ) : (
              isMobile ? <ShareIcon fontSize="small" /> : <PrintIcon fontSize="small" />
            )}
          </IconButton>
        </Tooltip>
        <Tooltip title="Cancel Bill">
          <IconButton
            size="small"
            onClick={() => setCancelModal(true, props.data)}
            sx={{
              color: '#b91010',
              backgroundColor: 'rgba(185, 16, 16, 0.1)',
              backdropFilter: 'blur(10px)',
              borderRadius: '8px',
              padding: '6px',
              '&:hover': {
                backgroundColor: 'rgba(185, 16, 16, 0.2)',
                transform: 'translateY(-2px)',
              },
              transition: 'all 0.2s ease',
            }}
          >
            <CancelIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
    );
  }, [setCancelModal, isSharing]);

  // ============ AG GRID COLUMNS ============
  const columnDefs = useMemo(() => [
    {
      headerName: '#',
      valueGetter: 'node.rowIndex + 1',
      width: 50,
      sortable: false,
      filter: false,
      pinned: 'left',
      cellStyle: {
        textAlign: 'center',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontWeight: 500,
        color: '#6B7280',
        textDecoration: params => params.data?.transactionDetails?.type === "cancelled" ? 'line-through' : 'none',
      },
    },
    {
      field: 'date',
      headerName: 'DATE',
      width: 110,
      sortable: true,
      filter: 'agDateColumnFilter',
      valueFormatter: (params) => {
        try {
          const date = parse(params.value, 'dd/MM/yyyy', new Date());
          return format(date, 'dd MMM');
        } catch {
          return params.value;
        }
      },
      cellStyle: {
        fontWeight: 600,
        color: '#374151',
        fontFamily: "'SF Mono', monospace",
      },
      headerClass: 'custom-header',
    },
    {
      field: 'customer',
      headerName: 'CUSTOMER',
      width: 250,
      sortable: true,
      filter: 'agTextColumnFilter',
      cellRenderer: CustomerNameRenderer,
      cellRendererParams: { context: { customers } },
      headerClass: 'custom-header',
    },
    {
      field: 'transactionDetails.billNo',
      headerName: 'BILL NO',
      width: 100,
      sortable: true,
      filter: 'agTextColumnFilter',
      cellStyle: {
        fontWeight: 700,
        color: '#1F2937',
        fontFamily: "'SF Mono', monospace",
      },
      headerClass: 'custom-header',
    },
    {
      field: 'items',
      headerName: 'ITEMS',
      width: 100,
      filter: false,
      sortable: false,
      cellRenderer: ItemsRenderer,
      headerClass: 'custom-header',
    },
    {
      field: 'summary.grandTotal',
      headerName: 'AMOUNT',
      width: 140,
      sortable: true,
      filter: 'agNumberColumnFilter',
      cellRenderer: AmountRenderer,
      headerClass: 'custom-header',
      comparator: (valueA, valueB) => valueA - valueB,
    },
    {
      headerName: 'FINE',
      width: 140,
      sortable: true,
      filter: false,
      cellRenderer: FineRenderer,
      valueGetter: (params) => {
        const t = params.data;
        return (
          t?.transactionDetails?.finePayment?.totalFinePaid ||
          t?.summary?.finePaid ||
          t?.paymentDetails?.finePayment?.totalFinePaid ||
          0
        );
      },
      comparator: (valueA, valueB) => valueA - valueB,
      headerClass: 'custom-header',
    },
    {
      field: 'summary.pendingAmount',
      headerName: 'STATUS',
      width: 120,
      filter: false,
      sortable: true,
      cellRenderer: StatusRenderer,
      comparator: (valueA, valueB) => (valueA === 0 ? -1 : valueB === 0 ? 1 : 0),
      headerClass: 'custom-header',
    },
    {
      headerName: 'ACTIONS',
      width: 140,
      filter: false,
      sortable: false,
      cellRenderer: ActionsRenderer,
      cellRendererParams: {
        context: {
          handleViewTransaction: (data) => {
            setSelectedTransaction(data);
            setOpenModal(true);
          },
          handlePrintBill: handlePrintBill,
        }
      },
      pinned: 'right',
      headerClass: 'custom-header',
    },
  ], [customers, handlePrintBill, ActionsRenderer]);

  const defaultColDef = useMemo(() => ({
    sortable: true,
    filter: true,
    resizable: true,
    flex: 1,
    minWidth: 100,
    wrapText: true,
    autoHeight: true,
    cellStyle: (params) => {
      const cancelled = isCancelledTransaction(params.data);
      return {
        color: cancelled ? (isDark ? '#9CA3AF' : '#6B7280') : theme.palette.text.primary,
        fontSize: '14px',
        display: 'flex',
        alignItems: 'center',
        whiteSpace: 'normal',
        wordWrap: 'break-word',
        textDecoration: cancelled ? 'line-through' : 'none',
        opacity: cancelled ? 0.8 : 1,
      };
    },
    headerClass: 'ag-header-cell-premium'
  }), [isDark, theme.palette.text.primary]);

  const gridOptions = useMemo(() => ({
    rowBuffer: 20,
    cacheBlockSize: 100,
    maxBlocksInCache: 10,
    animateRows: true,
    rowSelection: 'single',
    enableCellTextSelection: true,
    ensureDomOrder: true,
    suppressColumnVirtualisation: false,
    suppressRowVirtualisation: false,
    getRowStyle: (params) => {
      const cancelled = isCancelledTransaction(params.data);
      return {
        backgroundColor: params.node.rowIndex % 2 === 0
          ? (isDark ? 'rgba(255, 255, 255, 0.02)' : 'rgba(0, 0, 0, 0.02)')
          : 'transparent',
        textDecoration: cancelled ? 'line-through' : 'none',
        opacity: cancelled ? 0.6 : 1,
        color: cancelled ? (isDark ? '#9CA3AF' : '#6B7280') : theme.palette.text.primary,
      };
    },
    onGridReady: (params) => {
      params.api.sizeColumnsToFit();
      window.addEventListener('resize', () => {
        setTimeout(() => params.api.sizeColumnsToFit());
      });
    },
    onRowClicked: (params) => {
      params.node.setSelected(true, true);
    },
  }), [isDark, theme.palette.text.primary]);

  // ============ EXPORT PDF ============
  const handleExportPDF = useCallback(async () => {
    if (!gridRef.current?.api) {
      showAlert('Grid not ready', 'error');
      return;
    }
    if (filteredTransactions.length === 0) {
      showAlert('No transactions to export', 'warning');
      return;
    }

    setIsExporting(true);
    try {
      showAlert('Generating PDF...', 'info');
      const pdf = new jsPDF('p', 'mm', 'a4');
      const gstTransactions = filteredTransactions.filter(t =>
        t.transactionDetails?.gstApplicable
      );

      if (gstTransactions.length === 0) {
        showAlert('No GST transactions found for export', 'warning');
        return;
      }

      for (let i = 0; i < gstTransactions.length; i++) {
        if (i > 0) pdf.addPage();
        const transaction = gstTransactions[i];
        const billHtml = generateTransactionBillHTML(transaction, customers);
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = billHtml;
        tempDiv.style.width = '210mm';
        tempDiv.style.padding = '0';
        tempDiv.style.margin = '0';
        document.body.appendChild(tempDiv);

        const canvas = await html2canvas(tempDiv, {
          scale: 2,
          useCORS: true,
          logging: false
        });

        const imgData = canvas.toDataURL('image/jpeg', 0.9);
        const pageWidth = pdf.internal.pageSize.getWidth();
        const pageHeight = pdf.internal.pageSize.getHeight();
        const imgWidth = canvas.width;
        const imgHeight = canvas.height;
        const ratio = imgWidth / imgHeight;
        const margin = 5;
        const maxWidth = pageWidth - 2 * margin;
        const maxHeight = pageHeight - 2 * margin;

        let finalWidth, finalHeight;
        if (imgWidth > imgHeight) {
          finalWidth = maxWidth;
          finalHeight = maxWidth / ratio;
          if (finalHeight > maxHeight) {
            finalHeight = maxHeight;
            finalWidth = maxHeight * ratio;
          }
        } else {
          finalHeight = maxHeight;
          finalWidth = maxHeight * ratio;
          if (finalWidth > maxWidth) {
            finalWidth = maxWidth;
            finalHeight = maxWidth / ratio;
          }
        }

        const x = (pageWidth - finalWidth) / 2;
        const y = (pageHeight - finalHeight) / 2;
        pdf.addImage(imgData, 'JPEG', x, y, finalWidth, finalHeight);
        document.body.removeChild(tempDiv);
      }

      const fileName = `Transaction_History_${format(dates.startDate, 'dd-MM-yyyy')}_to_${format(dates.endDate, 'dd-MM-yyyy')}.pdf`;
      pdf.save(fileName);
      showAlert('PDF exported successfully', 'success');
    } catch (error) {
      console.error('Error generating PDF:', error);
      showAlert('Failed to generate PDF', 'error');
    } finally {
      setIsExporting(false);
    }
  }, [dates, customers, showAlert, filteredTransactions]);

  // ============ EXPORT EXCEL ============
  const handleExportExcel = useCallback(() => {
    if (!gridRef.current?.api) {
      showAlert('Grid not ready', 'error');
      return;
    }

    const calculateMetalWeights = (items) => {
      let goldWeight = 0;
      let silverWeight = 0;
      if (!items || !Array.isArray(items)) return { goldWeight, silverWeight };

      items.forEach(item => {
        const weight = parseFloat(item.weight) || 0;
        const possibleMetal = [
          item.metal,
          item.itemType,
          item.manualItemDetails?.metal,
          item.manualItemDetails?.itemType,
          item.manualItemDetails?.silverType,
          item.name
        ].filter(Boolean).map(str => str.toLowerCase());

        const isGold = possibleMetal.some(field => field === 'gold' || field.includes('gold'));
        const isSilver = possibleMetal.some(field => field === 'silver' || field.includes('silver'));

        if (isGold && isSilver) {
          const explicitMetal = [
            item.metal,
            item.manualItemDetails?.metal,
            item.manualItemDetails?.silverType
          ].filter(Boolean).map(str => str.toLowerCase());

          if (explicitMetal.some(f => f === 'gold' || f.includes('gold'))) goldWeight += weight;
          else if (explicitMetal.some(f => f === 'silver' || f.includes('silver'))) silverWeight += weight;
          else {
            const itemName = (item.name || '').toLowerCase();
            if (itemName.includes('gold')) goldWeight += weight;
            else if (itemName.includes('silver')) silverWeight += weight;
          }
        } else if (isGold) {
          goldWeight += weight;
        } else if (isSilver) {
          silverWeight += weight;
        }
      });

      return { goldWeight, silverWeight };
    };

    const formatItemDetails = (items) => {
      if (!items || !Array.isArray(items) || items.length === 0) return 'No items';

      return items.map((item, idx) => {
        const itemName = item.name || 'Unknown Item';
        const weight = parseFloat(item.weight) || 0;
        const metalRate = parseFloat(item.metalRate) || 0;
        const total = parseFloat(item.total) || 0;
        const huid = item.huid || '';
        const carat = item.ct || '';

        let description = `${idx + 1}. ${itemName}`;
        if (carat) description += ` (${carat})`;
        if (huid) description += ` [HUID:${huid}]`;
        description += ` - ${weight}g`;
        if (metalRate) description += ` @ ₹${metalRate}`;
        description += ` = ₹${total.toFixed(2)}`;
        if (item.wastage && parseFloat(item.wastage) > 0) {
          description += ` (MC:${item.wastage}%)`;
        } else if (item.makingCharges && parseFloat(item.makingCharges) > 0) {
          description += ` (Labour:₹${item.makingCharges}/g)`;
        }
        return description;
      }).join('\n');
    };

    const gstTransactions = filteredTransactions.filter(transaction => {
      const isGST = transaction.transactionDetails?.gstApplicable === true;
      const cancelled = isCancelledTransaction(transaction);
      return isGST && !cancelled;
    });

    if (gstTransactions.length === 0) {
      showAlert('No GST transactions found to export', 'warning');
      return;
    }

    try {
      const excelData = gstTransactions.map((transaction, index) => {
        const customer = customers.find(c => c.id === transaction.customer);
        const { goldWeight, silverWeight } = calculateMetalWeights(transaction.items);
        const itemDetails = formatItemDetails(transaction.items);

        return {
          'Sr. No.': index + 1,
          'Date': transaction.date,
          'Customer Name': customer?.name || 'N/A',
          'Bill No': transaction.transactionDetails?.billNo || 'N/A',
          'Items Count': transaction.items?.length || 0,
          'Item Details': itemDetails,
          'Gold Weight (g)': goldWeight.toFixed(2),
          'Silver Weight (g)': silverWeight.toFixed(2),
          'Subtotal': transaction.calculatedTotals?.subtotal || 0,
          'IGST': transaction.summary?.igst || 0,
          'CGST': transaction.summary?.cgst || 0,
          'SGST': transaction.summary?.sgst || 0,
          'GST Type': transaction.transactionDetails?.gstApplicable
            ? (transaction.transactionDetails?.gstType === 'igst' ? 'IGST' : 'CGST/SGST')
            : 'No GST',
          'Grand Total': transaction.summary?.grandTotal || 0,
          'Paid': transaction.summary?.paymentDone || 0,
          'Pending': transaction.summary?.pendingAmount || 0,
          'Status': (transaction.summary?.pendingAmount || 0) === 0 ? 'Paid' : 'Pending',
          'GST': transaction.transactionDetails?.gstApplicable ? 'Yes' : 'No',
        };
      });

      const totals = {
        'Sr. No.': 'TOTAL',
        'Date': '',
        'Customer Name': '',
        'Bill No': '',
        'Items Count': excelData.reduce((sum, row) => sum + row['Items Count'], 0),
        'Item Details': 'Total Summary',
        'Gold Weight (g)': excelData.reduce((sum, row) => sum + parseFloat(row['Gold Weight (g)'] || 0), 0).toFixed(2),
        'Silver Weight (g)': excelData.reduce((sum, row) => sum + parseFloat(row['Silver Weight (g)'] || 0), 0).toFixed(2),
        'Subtotal': excelData.reduce((sum, row) => sum + row['Subtotal'], 0),
        'IGST': excelData.reduce((sum, row) => sum + row['IGST'], 0),
        'CGST': excelData.reduce((sum, row) => sum + row['CGST'], 0),
        'SGST': excelData.reduce((sum, row) => sum + row['SGST'], 0),
        'GST Type': '',
        'Grand Total': excelData.reduce((sum, row) => sum + row['Grand Total'], 0),
        'Paid': excelData.reduce((sum, row) => sum + row['Paid'], 0),
        'Pending': excelData.reduce((sum, row) => sum + row['Pending'], 0),
        'Status': '',
        'GST': '',
      };

      excelData.push({});
      excelData.push(totals);

      const headers = Object.keys(excelData[0]);
      const dataWithHeaders = [
        headers,
        ...excelData.map(row => headers.map(key => row[key]))
      ];

      const worksheet = XLSX.utils.aoa_to_sheet(dataWithHeaders);

      const colWidths = [
        { wch: 8 }, { wch: 12 }, { wch: 25 }, { wch: 10 }, { wch: 12 },
        { wch: 60 }, { wch: 15 }, { wch: 15 }, { wch: 15 }, { wch: 12 },
        { wch: 12 }, { wch: 12 }, { wch: 14 }, { wch: 15 }, { wch: 12 },
        { wch: 12 }, { wch: 10 }, { wch: 8 },
      ];
      worksheet['!cols'] = colWidths;

      if (worksheet['!rows'] === undefined) worksheet['!rows'] = [];

      const itemDetailsColIndex = headers.indexOf('Item Details');
      if (itemDetailsColIndex !== -1) {
        for (let row = 1; row <= dataWithHeaders.length; row++) {
          const cellAddress = XLSX.utils.encode_cell({ r: row, c: itemDetailsColIndex });
          if (worksheet[cellAddress]) {
            worksheet[cellAddress].s = {
              alignment: { wrapText: true, vertical: "top", horizontal: "left" }
            };
          }
        }
      }

      const headerRange = XLSX.utils.decode_range(worksheet['!ref']);
      headerRange.s.r = 0;
      headerRange.e.r = 0;

      for (let C = headerRange.s.c; C <= headerRange.e.c; ++C) {
        const cellAddress = XLSX.utils.encode_cell({ r: 0, c: C });
        if (!worksheet[cellAddress]) continue;
        worksheet[cellAddress].s = {
          font: { bold: true, sz: 11 },
          fill: { fgColor: { rgb: "E0E0E0" } },
          alignment: { horizontal: "center", vertical: "center" }
        };
      }

      worksheet['!rows'] = worksheet['!rows'] || [];
      for (let row = 1; row < dataWithHeaders.length; row++) {
        worksheet['!rows'][row] = { hpt: 45 };
      }

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'GST Transactions');

      const fileName = `GST_Transactions_${format(dates.startDate, 'dd-MM-yyyy')}_to_${format(dates.endDate, 'dd-MM-yyyy')}.xlsx`;
      XLSX.writeFile(workbook, fileName);

      showAlert(`Exported ${gstTransactions.length} GST transactions successfully`, 'success');
    } catch (error) {
      console.error('Error exporting Excel:', error);
      showAlert('Failed to export Excel file', 'error');
    }
  }, [customers, dates, showAlert, filteredTransactions]);

  const handleClearFilters = useCallback(() => {
    setCustomerFilter('all');
    setSearchInput('');
    showAlert('Filters cleared', 'info');
  }, [showAlert]);

  // ============ OVERLAYS ============
  const loadingOverlayComponent = useMemo(() => {
    return function CustomLoadingOverlay() {
      return (
        <Box
          sx={{
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backdropFilter: 'blur(10px)',
          }}
        >
          <Box sx={{ textAlign: 'center' }}>
            <CircularProgress size={50} sx={{ color: '#2563EB', mb: 2 }} />
            <Typography
              sx={{
                fontSize: '1rem',
                fontWeight: 600,
                color: isDark ? '#e0e0e0' : '#1a1a1a',
              }}
            >
              Loading transactions...
            </Typography>
          </Box>
        </Box>
      );
    };
  }, [isDark]);

  const noRowsOverlayComponent = useMemo(() => {
    return function CustomNoRowsOverlay() {
      return (
        <Box
          sx={{
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexDirection: 'column',
            gap: 2,
          }}
        >
          <Box sx={{
            width: 80,
            height: 80,
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: isDark
              ? 'linear-gradient(135deg, rgba(30, 41, 59, 0.4), rgba(30, 41, 59, 0.6))'
              : 'linear-gradient(135deg, rgba(255, 255, 255, 0.8), rgba(255, 255, 255, 0.9))',
            backdropFilter: 'blur(10px)',
            border: isDark
              ? '1px solid rgba(255, 255, 255, 0.1)'
              : '1px solid rgba(0, 0, 0, 0.1)',
          }}>
            <ReceiptIcon sx={{
              fontSize: 40,
              color: isDark ? '#94A3B8' : '#9CA3AF',
              opacity: 0.6,
            }} />
          </Box>
          <Typography
            variant="h6"
            sx={{ color: isDark ? '#A0A0A0' : '#666', fontWeight: 500 }}
          >
            {searchInput ? 'No transactions found' : 'No transactions available'}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: isDark ? '#71717A' : '#9CA3AF',
              textAlign: 'center',
              maxWidth: 300,
            }}
          >
            {searchInput
              ? 'Try adjusting your search or filters to find what you\'re looking for.'
              : 'Transactions will appear here once you create them.'}
          </Typography>
        </Box>
      );
    };
  }, [isDark, searchInput]);

  const isLoading = contextLoading || isPageLoading;
  const isProcessing = isSearching || isFiltering || isExporting;

  if (isLoading) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Box sx={{ textAlign: 'center' }}>
          <CircularProgress size={60} thickness={4} sx={{ color: '#2563EB', mb: 2 }} />
          <Typography
            sx={{
              fontSize: '1.1rem',
              fontWeight: 600,
              background: isDark
                ? 'linear-gradient(135deg, #60A5FA, #93C5FD)'
                : 'linear-gradient(135deg, #2563EB, #3B82F6)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
            }}
          >
            Loading transactions...
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ minHeight: '100vh', py: 4 }}>
        <Backdrop
          open={isExporting}
          sx={{
            zIndex: (theme) => theme.zIndex.drawer + 1,
            color: '#fff',
            backdropFilter: 'blur(10px)',
          }}
        >
          <Box sx={{ textAlign: 'center' }}>
            <CircularProgress color="inherit" size={50} />
            <Typography sx={{ mt: 2, fontWeight: 600 }}>Generating PDF...</Typography>
            <Typography variant="body2" sx={{ mt: 1, opacity: 0.8 }}>
              This may take a moment
            </Typography>
          </Box>
        </Backdrop>

        <Container maxWidth="xl">
          {/* Alert */}
          {alertMessage && (
            <Fade in={!!alertMessage}>
              <Alert
                severity={alertType}
                sx={{
                  mb: 3,
                  borderRadius: '12px',
                  backdropFilter: 'blur(10px)',
                  border: isDark
                    ? '1px solid rgba(255, 255, 255, 0.1)'
                    : '1px solid rgba(0, 0, 0, 0.1)',
                  backgroundColor: isDark
                    ? 'rgba(30, 41, 59, 0.7)'
                    : 'rgba(255, 255, 255, 0.8)',
                }}
                onClose={() => setAlertMessage('')}
              >
                {alertMessage}
              </Alert>
            </Fade>
          )}

          {/* Header */}
          <Box sx={{
            mb: 4,
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            justifyContent: 'space-between',
            alignItems: { xs: 'stretch', md: 'center' },
            gap: 3,
          }}>
            <Box>
              <Typography
                variant="h3"
                sx={{
                  fontWeight: 900,
                  mb: 0.5,
                  background: isDark
                    ? 'linear-gradient(135deg, #F1F5F9, #E2E8F0)'
                    : 'linear-gradient(135deg, #1E293B, #374151)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                }}
              >
                Transaction History
              </Typography>
              <Typography
                sx={{
                  color: isDark ? '#A0A0A0' : '#6B7280',
                  fontSize: '1.1rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                }}
              >
                <HistoryIcon fontSize="small" />
                View and manage customer transactions
              </Typography>
            </Box>
            <Box sx={{
              display: 'flex',
              gap: 2,
              flexWrap: 'wrap',
              justifyContent: { xs: 'center', md: 'flex-end' },
            }}>
              <Button
                startIcon={<RefreshIcon />}
                onClick={refresh}
                variant="outlined"
                disabled={isFiltering}
                sx={{
                  borderColor: theme.palette.mode === 'dark' ? '#60A5FA' : '#2563EB',
                  color: theme.palette.mode === 'dark' ? '#60A5FA' : '#fff',
                  borderRadius: '10px',
                  backdropFilter: 'blur(10px)',
                  backgroundColor: theme.palette.mode === 'dark'
                    ? 'rgba(30, 41, 59, 0.8)'
                    : 'rgba(37, 99, 235, 0.8)',
                  borderWidth: '1.5px',
                  fontWeight: 600,
                  '&:hover': {
                    backgroundColor: theme.palette.mode === 'dark'
                      ? 'rgba(96, 165, 250, 0.25)'
                      : 'rgba(37, 99, 235, 0.25)',
                    transform: 'translateY(-2px)',
                  },
                  transition: 'all 0.2s ease',
                }}
              >
                Refresh
              </Button>

              <Button
                startIcon={<ClearIcon />}
                onClick={handleClearFilters}
                variant="outlined"
                disabled={customerFilter === 'all' && !searchInput}
                sx={{
                  borderColor: theme.palette.mode === 'dark' ? '#94A3B8' : '#6B7280',
                  color: theme.palette.mode === 'dark' ? '#E2E8F0' : '#fff',
                  borderRadius: '10px',
                  backdropFilter: 'blur(10px)',
                  backgroundColor: theme.palette.mode === 'dark'
                    ? 'rgba(30, 41, 59, 0.8)'
                    : 'rgba(107, 114, 128, 0.8)',
                  borderWidth: '1.5px',
                  fontWeight: 600,
                  '&:hover': {
                    backgroundColor: theme.palette.mode === 'dark'
                      ? 'rgba(148, 163, 184, 0.25)'
                      : 'rgba(107, 114, 128, 0.25)',
                    transform: 'translateY(-2px)',
                  },
                  transition: 'all 0.2s ease',
                }}
              >
                Clear Filters
              </Button>

              <Button
                startIcon={<DownloadIcon />}
                onClick={handleExportExcel}
                variant="outlined"
                disabled={filteredTransactions.length === 0}
                sx={{
                  borderColor: theme.palette.mode === 'dark' ? '#34D399' : '#10B981',
                  color: theme.palette.mode === 'dark' ? '#34D399' : '#fff',
                  borderRadius: '10px',
                  backdropFilter: 'blur(10px)',
                  backgroundColor: theme.palette.mode === 'dark'
                    ? 'rgba(30, 41, 59, 0.8)'
                    : 'rgba(16, 185, 129, 0.8)',
                  borderWidth: '1.5px',
                  fontWeight: 600,
                  '&:hover': {
                    backgroundColor: theme.palette.mode === 'dark'
                      ? 'rgba(52, 211, 153, 0.25)'
                      : 'rgba(16, 185, 129, 0.25)',
                    transform: 'translateY(-2px)',
                  },
                  transition: 'all 0.2s ease',
                }}
              >
                Export Excel
              </Button>

              <Button
                startIcon={<PdfIcon />}
                onClick={handleExportPDF}
                variant="contained"
                disabled={isExporting || filteredTransactions.length === 0}
                sx={{
                  background: 'linear-gradient(135deg, #DC2626, #B91C1C)',
                  borderRadius: '10px',
                  boxShadow: '0 4px 12px rgba(220, 38, 38, 0.4)',
                  fontWeight: 600,
                  textShadow: '0 1px 2px rgba(0, 0, 0, 0.3)',
                  border: '1px solid rgba(220, 38, 38, 0.4)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #B91C1C, #991B1B)',
                    boxShadow: '0 6px 20px rgba(220, 38, 38, 0.5)',
                    transform: 'translateY(-2px)',
                  },
                  transition: 'all 0.2s ease',
                }}
              >
                Export PDF
              </Button>
            </Box>
          </Box>

          {/* Stats Summary */}
          <Grid container spacing={2} sx={{ mb: 4 }}>
            {/* 1. Total Transactions */}
            <Grid item xs={12} sm={6} md={2.4}>
              <Card sx={{
                borderRadius: '16px',
                background: isDark
                  ? 'linear-gradient(135deg, rgba(30, 41, 59, 0.6), rgba(30, 41, 59, 0.6))'
                  : 'linear-gradient(135deg, rgba(255, 255, 255, 0.8), rgba(255, 255, 255, 0.6))',
                backdropFilter: 'blur(10px)',
                border: isDark ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid rgba(0, 0, 0, 0.08)',
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
                transition: 'all 0.3s ease',
                '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 12px 40px rgba(0, 0, 0, 0.15)' },
                height: '100%',
              }}>
                <CardContent sx={{ p: 2.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                    <Box sx={{
                      width: 36, height: 36, borderRadius: '10px',
                      background: 'linear-gradient(135deg, rgba(37, 99, 235, 0.15), rgba(59, 130, 246, 0.15))',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', mr: 1.5,
                    }}>
                      <ReceiptIcon sx={{ color: '#2563EB', fontSize: 18 }} />
                    </Box>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                      Total Txns
                    </Typography>
                  </Box>
                  <Typography variant="h5" sx={{ fontWeight: 800, color: '#2563EB', mb: 0.5 }}>
                    {filteredTransactions.length}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                    {filteredTransactions.length > 0 ? (
                      <>
                        <TrendingUpIcon sx={{ fontSize: '10px', verticalAlign: 'middle', mr: 0.5 }} />
                        {((filteredTransactions.length / customerTransactions.length) * 100).toFixed(1)}% of total
                      </>
                    ) : 'No transactions'}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            {/* 2. Total Amount */}
            <Grid item xs={12} sm={6} md={2.4}>
              <Card sx={{
                borderRadius: '16px',
                background: isDark
                  ? 'linear-gradient(135deg, rgba(30, 41, 59, 0.6), rgba(30, 41, 59, 0.6))'
                  : 'linear-gradient(135deg, rgba(255, 255, 255, 0.8), rgba(255, 255, 255, 0.6))',
                backdropFilter: 'blur(10px)',
                border: isDark ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid rgba(0, 0, 0, 0.08)',
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
                transition: 'all 0.3s ease',
                '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 12px 40px rgba(0, 0, 0, 0.15)' },
                height: '100%',
              }}>
                <CardContent sx={{ p: 2.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                    <Box sx={{
                      width: 36, height: 36, borderRadius: '10px',
                      background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.15), rgba(34, 197, 94, 0.15))',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', mr: 1.5,
                    }}>
                      <TrendingUpIcon sx={{ color: '#10B981', fontSize: 18 }} />
                    </Box>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                      Total Amount
                    </Typography>
                  </Box>
                  <Typography variant="h5" sx={{ fontWeight: 800, color: '#10B981', mb: 0.5 }}>
                    {new Intl.NumberFormat('en-IN', {
                      style: 'currency', currency: 'INR', minimumFractionDigits: 0,
                    }).format(filteredTransactions.reduce((sum, t) => sum + parseInt(t.summary?.grandTotal || 0), 0))}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                    Avg: {new Intl.NumberFormat('en-IN', {
                      style: 'currency', currency: 'INR', minimumFractionDigits: 0,
                    }).format(filteredTransactions.length > 0 ?
                      filteredTransactions.reduce((sum, t) => sum + parseInt(t.summary?.grandTotal || 0), 0) / filteredTransactions.length : 0)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            {/* 3. Fine Amount */}
            <Grid item xs={12} sm={6} md={2.4}>
              <Card sx={{
                borderRadius: '16px',
                background: isDark
                  ? 'linear-gradient(135deg, rgba(139, 92, 246, 0.15), rgba(124, 58, 237, 0.08))'
                  : 'linear-gradient(135deg, rgba(139, 92, 246, 0.1), rgba(124, 58, 237, 0.05))',
                backdropFilter: 'blur(10px)',
                border: isDark
                  ? '1px solid rgba(167, 139, 250, 0.3)'
                  : '1px solid rgba(139, 92, 246, 0.2)',
                boxShadow: '0 8px 32px rgba(139, 92, 246, 0.15)',
                transition: 'all 0.3s ease',
                '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 12px 40px rgba(139, 92, 246, 0.25)' },
                height: '100%',
              }}>
                <CardContent sx={{ p: 2.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                    <Box sx={{
                      width: 36, height: 36, borderRadius: '10px',
                      background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.2), rgba(167, 139, 250, 0.2))',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', mr: 1.5,
                    }}>
                      <DiamondIcon sx={{ color: '#8B5CF6', fontSize: 18 }} />
                    </Box>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                      Fine Amount
                    </Typography>
                  </Box>
                  <Typography variant="h5" sx={{ fontWeight: 800, color: '#8B5CF6', mb: 0.5 }}>
                    {filteredTransactions
                      .reduce((sum, t) => {
                        const finePaid =
                          t?.transactionDetails?.finePayment?.totalFinePaid ||
                          t?.summary?.finePaid ||
                          t?.paymentDetails?.finePayment?.totalFinePaid ||
                          0;
                        return sum + parseFloat(finePaid || 0);
                      }, 0)
                      .toFixed(3)} g
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                    {filteredTransactions.filter(t => {
                      const fineBal =
                        t?.transactionDetails?.finePayment?.balanceFine ??
                        t?.summary?.fineBalance ??
                        t?.paymentDetails?.finePayment?.balanceFine ??
                        0;
                      return fineBal > 0;
                    }).length} pending fine
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            {/* 4. Total Pending */}
            <Grid item xs={12} sm={6} md={2.4}>
              <Card sx={{
                borderRadius: '16px',
                background: isDark
                  ? 'linear-gradient(135deg, rgba(30, 41, 59, 0.6), rgba(30, 41, 59, 0.6))'
                  : 'linear-gradient(135deg, rgba(255, 255, 255, 0.8), rgba(255, 255, 255, 0.6))',
                backdropFilter: 'blur(10px)',
                border: isDark ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid rgba(0, 0, 0, 0.08)',
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
                transition: 'all 0.3s ease',
                '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 12px 40px rgba(0, 0, 0, 0.15)' },
                height: '100%',
              }}>
                <CardContent sx={{ p: 2.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                    <Box sx={{
                      width: 36, height: 36, borderRadius: '10px',
                      background: 'linear-gradient(135deg, rgba(239, 68, 68, 0.15), rgba(220, 38, 38, 0.15))',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', mr: 1.5,
                    }}>
                      <PaymentIcon sx={{ color: '#EF4444', fontSize: 18 }} />
                    </Box>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                      Total Pending
                    </Typography>
                  </Box>
                  <Typography variant="h5" sx={{ fontWeight: 800, color: '#EF4444', mb: 0.5 }}>
                    {new Intl.NumberFormat('en-IN', {
                      style: 'currency', currency: 'INR', minimumFractionDigits: 0,
                    }).format(filteredTransactions.reduce((sum, t) => sum + parseInt(t.summary?.pendingAmount || 0), 0))}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                    {filteredTransactions.filter(t => t.summary?.pendingAmount > 0).length} pending
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            {/* 5. Fully Paid */}
            <Grid item xs={12} sm={6} md={2.4}>
              <Card sx={{
                borderRadius: '16px',
                background: isDark
                  ? 'linear-gradient(135deg, rgba(30, 41, 59, 0.6), rgba(30, 41, 59, 0.6))'
                  : 'linear-gradient(135deg, rgba(255, 255, 255, 0.8), rgba(255, 255, 255, 0.6))',
                backdropFilter: 'blur(10px)',
                border: isDark ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid rgba(0, 0, 0, 0.08)',
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
                transition: 'all 0.3s ease',
                '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 12px 40px rgba(0, 0, 0, 0.15)' },
                height: '100%',
              }}>
                <CardContent sx={{ p: 2.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                    <Box sx={{
                      width: 36, height: 36, borderRadius: '10px',
                      background: 'linear-gradient(135deg, rgba(34, 197, 94, 0.15), rgba(22, 163, 74, 0.15))',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', mr: 1.5,
                    }}>
                      <CheckCircleIcon sx={{ color: '#22C55E', fontSize: 18 }} />
                    </Box>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                      Fully Paid
                    </Typography>
                  </Box>
                  <Typography variant="h5" sx={{ fontWeight: 800, color: '#22C55E', mb: 0.5 }}>
                    {filteredTransactions.filter(t => {
                      const pendingAmt = t.summary?.pendingAmount || 0;
                      const fineBal =
                        t?.transactionDetails?.finePayment?.balanceFine ??
                        t?.summary?.fineBalance ??
                        0;
                      return pendingAmt === 0 && fineBal === 0;
                    }).length}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                    {filteredTransactions.length > 0 ?
                      ((filteredTransactions.filter(t => {
                        const pendingAmt = t.summary?.pendingAmount || 0;
                        const fineBal =
                          t?.transactionDetails?.finePayment?.balanceFine ??
                          t?.summary?.fineBalance ??
                          0;
                        return pendingAmt === 0 && fineBal === 0;
                      }).length / filteredTransactions.length) * 100).toFixed(1) : 0}% paid
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Active Filters Summary */}
          {(customerFilter !== 'all' || searchInput) && (
            <Fade in>
              <Box sx={{
                mb: 3,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                flexWrap: 'wrap',
                padding: 2,
                borderRadius: '12px',
                background: isDark
                  ? 'linear-gradient(135deg, rgba(30, 41, 59, 0.6), rgba(30, 41, 59, 0.6))'
                  : 'linear-gradient(135deg, rgba(255, 255, 255, 0.8), rgba(255, 255, 255, 0.6))',
                backdropFilter: 'blur(10px)',
                border: isDark
                  ? '1px solid rgba(255, 255, 255, 0.1)'
                  : '1px solid rgba(0, 0, 0, 0.08)',
              }}>
                <FilterIcon sx={{ color: '#6B7280', fontSize: '1rem' }} />
                <Typography variant="body2" color="text.secondary">
                  Active filters:
                </Typography>

                {customerFilter !== 'all' && (
                  <Chip
                    label={`Customer: ${customers.find(c => c.id === customerFilter)?.name || 'Selected Customer'}`}
                    size="small"
                    onDelete={() => setCustomerFilter('all')}
                    color="primary"
                    variant="filled"
                    sx={{
                      backdropFilter: 'blur(10px)',
                      backgroundColor: 'rgba(37, 99, 235, 0.1)',
                      border: '1px solid rgba(37, 99, 235, 0.3)',
                      color: '#2563EB',
                      fontWeight: 500,
                    }}
                  />
                )}

                {searchInput && (
                  <Chip
                    label={`Search: "${searchInput}"`}
                    size="small"
                    onDelete={() => setSearchInput('')}
                    color="secondary"
                    variant="filled"
                    sx={{
                      backdropFilter: 'blur(10px)',
                      backgroundColor: 'rgba(139, 92, 246, 0.1)',
                      border: '1px solid rgba(139, 92, 246, 0.3)',
                      color: '#8B5CF6',
                      fontWeight: 500,
                    }}
                  />
                )}

                <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
                  Showing {filteredTransactions.length} of {customerTransactions.length} transactions
                </Typography>
              </Box>
            </Fade>
          )}

          {/* Filters Section */}
          <Card sx={{
            mb: 4,
            borderRadius: '16px',
            background: isDark
              ? 'linear-gradient(135deg, rgba(30, 41, 59, 0.6), rgba(30, 41, 59, 0.6))'
              : 'linear-gradient(135deg, rgba(255, 255, 255, 0.8), rgba(255, 255, 255, 0.6))',
            backdropFilter: 'blur(10px)',
            border: isDark
              ? '1px solid rgba(255, 255, 255, 0.1)'
              : '1px solid rgba(0, 0, 0, 0.08)',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
          }}>
            <CardContent sx={{ p: 3 }}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={4}>
                  <DatePicker
                    label="Start Date"
                    value={dates.startDate}
                    onChange={(newValue) => setDates(prev => ({ ...prev, startDate: newValue }))}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        fullWidth
                        size="small"
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <CalendarIcon />
                            </InputAdornment>
                          ),
                        }}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: '10px',
                            backdropFilter: 'blur(10px)',
                            backgroundColor: isDark
                              ? 'rgba(255, 255, 255, 0.05)'
                              : 'rgba(0, 0, 0, 0.02)',
                          },
                        }}
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <DatePicker
                    label="End Date"
                    value={dates.endDate}
                    onChange={(newValue) => setDates(prev => ({ ...prev, endDate: newValue }))}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        fullWidth
                        size="small"
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <CalendarIcon />
                            </InputAdornment>
                          ),
                        }}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: '10px',
                            backdropFilter: 'blur(10px)',
                            backgroundColor: isDark
                              ? 'rgba(255, 255, 255, 0.05)'
                              : 'rgba(0, 0, 0, 0.02)',
                          },
                        }}
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <Autocomplete
                    size="small"
                    options={[{ id: 'all', name: 'All Customers' }, ...customers]}
                    value={customerFilter === 'all'
                      ? { id: 'all', name: 'All Customers' }
                      : customers.find(c => c.id === customerFilter) || null}
                    getOptionLabel={(option) => option.name}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    onChange={(event, newValue) => {
                      setCustomerFilter(newValue ? newValue.id : 'all');
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Customer"
                        placeholder="Search by name, phone, address..."
                        InputProps={{
                          ...params.InputProps,
                          startAdornment: (
                            <InputAdornment position="start">
                              <SearchIcon />
                            </InputAdornment>
                          ),
                        }}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: '10px',
                            backdropFilter: 'blur(10px)',
                            backgroundColor: isDark
                              ? 'rgba(255, 255, 255, 0.05)'
                              : 'rgba(0, 0, 0, 0.02)',
                            width: '300px',
                          },
                        }}
                      />
                    )}
                    renderOption={(props, option) => {
                      if (option.id === 'all') {
                        return (
                          <Box component="li" {...props} key="all">
                            <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                              {option.name}
                            </Typography>
                          </Box>
                        );
                      }
                      return (
                        <Box component="li" {...props} key={option.id}>
                          <Box sx={{ width: '100%' }}>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {option.name}
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                              {option.contact && (
                                <Chip
                                  size="small"
                                  label={option.contact}
                                  variant="outlined"
                                  sx={{
                                    height: 20,
                                    fontSize: '0.7rem',
                                    backdropFilter: 'blur(10px)',
                                  }}
                                />
                              )}
                            </Box>
                          </Box>
                        </Box>
                      );
                    }}
                    filterOptions={(options, { inputValue }) => {
                      if (!inputValue) return options.slice(0, 50);
                      const searchTerm = inputValue.toLowerCase();
                      return options.filter(option => {
                        if (option.id === 'all') return true;
                        return (
                          option.name.toLowerCase().includes(searchTerm) ||
                          option.contact?.toString().includes(searchTerm)
                        );
                      });
                    }}
                    noOptionsText={
                      <Typography variant="body2" color="text.secondary" sx={{ p: 2, textAlign: 'center' }}>
                        No matching customers found
                      </Typography>
                    }
                    loading={customers.length === 0}
                    loadingText="Loading customers..."
                    blurOnSelect
                    clearOnBlur={false}
                    sx={{
                      '& .MuiAutocomplete-listbox': {
                        maxHeight: 400,
                        backdropFilter: 'blur(10px)',
                        backgroundColor: isDark
                          ? 'rgba(30, 41, 59, 0.95)'
                          : 'rgba(255, 255, 255, 0.95)',
                      },
                    }}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* AG Grid Table */}
          <Card
            sx={{
              borderRadius: '16px',
              overflow: 'hidden',
              position: 'relative',
              height: 'calc(100vh - 300px)',
              minHeight: '500px',
              background: isDark
                ? 'linear-gradient(135deg, rgba(30, 41, 59, 0.6), rgba(30, 41, 59, 0.2))'
                : 'linear-gradient(135deg, rgba(255, 255, 255, 0.8), rgba(255, 255, 255, 0.2))',
              backdropFilter: 'blur(10px)',
              border: isDark
                ? '1px solid rgba(255, 255, 255, 0.1)'
                : '1px solid rgba(0, 0, 0, 0.08)',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
            }}
          >
            {isProcessing && (
              <LinearProgress
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  zIndex: 2,
                  height: 3,
                  background: 'linear-gradient(90deg, #2563EB, #3B82F6)',
                  '& .MuiLinearProgress-bar': {
                    background: 'linear-gradient(90deg, #3B82F6, #60A5FA)',
                  },
                }}
              />
            )}

            <CardContent sx={{ p: 0, height: '100%' }}>
              <div
                className={`ag-theme-premium-${theme.palette.mode}`}
                style={{
                  width: '100%',
                  height: '100%',
                }}
              >
                <AgGridReact
                  ref={gridRef}
                  columnDefs={columnDefs}
                  rowData={filteredTransactions}
                  defaultColDef={defaultColDef}
                  gridOptions={gridOptions}
                  loadingOverlayComponent={loadingOverlayComponent}
                  noRowsOverlayComponent={noRowsOverlayComponent}
                  headerHeight={50}
                  rowHeight={64}
                  animateRows={true}
                  enableCellTextSelection={true}
                  ensureDomOrder={true}
                  suppressContextMenu={true}
                  suppressMenuHide={false}
                  suppressMovableColumns={true}
                  getRowStyle={(params) => ({
                    borderBottom: isDark
                      ? '1px solid rgba(255, 255, 255, 0.05)'
                      : '1px solid rgba(0, 0, 0, 0.03)',
                    transition: 'background-color 0.2s ease',
                  })}
                  onGridReady={(params) => {
                    const headerElements = document.querySelectorAll('.ag-header-cell');
                    headerElements.forEach(header => {
                      header.style.backdropFilter = 'blur(10px)';
                      header.style.borderRight = 'none';
                      header.style.fontWeight = '600';
                      header.style.letterSpacing = '0.5px';
                      header.style.textTransform = 'uppercase';
                      header.style.fontSize = '11px';
                      header.style.color = isDark ? '#94A3B8' : '#6B7280';
                    });
                  }}
                />
              </div>
            </CardContent>
          </Card>

          {/* ============ TRANSACTION DETAIL MODAL (EXTRACTED COMPONENT) ============ */}
          <TransactionDetailModal
            open={openModal}
            onClose={() => setOpenModal(false)}
            transaction={selectedTransaction}
            customers={customers}
            isDark={isDark}
          />
        </Container>
      </Box>

      {/* ============ CANCEL BILL MODAL ============ */}
      <Dialog
        open={cancelModalOpen}
        onClose={handleCancelModalClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '16px',
            background: isDark
              ? 'linear-gradient(135deg, rgba(30, 41, 59, 0.95), rgba(30, 41, 59, 0.98))'
              : 'linear-gradient(135deg, rgba(255, 255, 255, 0.95), rgba(255, 255, 255, 0.98))',
            backdropFilter: 'blur(20px)',
            border: isDark
              ? '1px solid rgba(255, 255, 255, 0.1)'
              : '1px solid rgba(0, 0, 0, 0.1)',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
          }
        }}
      >
        <DialogTitle sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          pb: 2,
          borderBottom: isDark
            ? '1px solid rgba(255, 255, 255, 0.1)'
            : '1px solid rgba(0, 0, 0, 0.1)',
        }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, color: '#DC2626' }}>
              Cancel Bill #{selectedCancelTransaction?.transactionDetails?.billNo}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              This action cannot be undone
            </Typography>
          </Box>
          <IconButton
            onClick={handleCancelModalClose}
            size="small"
            disabled={isCancelling}
            sx={{
              backgroundColor: 'rgba(0, 0, 0, 0.05)',
              '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.1)' },
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers sx={{ py: 3 }}>
          {selectedCancelTransaction && (
            <Grid container spacing={3}>
              {/* Bill Information */}
              <Grid item xs={12}>
                <Card sx={{
                  borderRadius: '12px',
                  background: 'rgba(220, 38, 38, 0.05)',
                  border: '1px solid rgba(220, 38, 38, 0.2)',
                }}>
                  <CardContent>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#DC2626', mb: 1 }}>
                      Bill Information
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary">Bill No</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {selectedCancelTransaction.transactionDetails?.billNo}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary">Original Amount</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          ₹{selectedCancelTransaction.summary?.grandTotal?.toLocaleString('en-IN')}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary">Date</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {selectedCancelTransaction.date}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary">Paid Amount</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 500, color: '#059669' }}>
                          ₹{selectedCancelTransaction.summary?.paymentDone?.toLocaleString('en-IN') || selectedCancelTransaction.summary?.grandTotal?.toLocaleString('en-IN')}
                        </Typography>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>

              {/* Refund Amount Section */}
              <Grid item xs={12}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                  Refund Amount
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Cash Refund (₹)"
                      value={refundAmounts.cash}
                      onChange={(e) => handleRefundChange('cash', e.target.value)}
                      disabled={isCancelling}
                      InputProps={{
                        startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '10px',
                          backgroundColor: isDark
                            ? 'rgba(255, 255, 255, 0.05)'
                            : 'rgba(0, 0, 0, 0.02)',
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Bank Transfer (₹)"
                      value={refundAmounts.bank}
                      onChange={(e) => handleRefundChange('bank', e.target.value)}
                      disabled={isCancelling}
                      InputProps={{
                        startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '10px',
                          backgroundColor: isDark
                            ? 'rgba(255, 255, 255, 0.05)'
                            : 'rgba(0, 0, 0, 0.02)',
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Card sx={{
                      mt: 2,
                      borderRadius: '10px',
                      background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.1), rgba(34, 197, 94, 0.1))',
                      border: '1px solid rgba(16, 185, 129, 0.2)',
                    }}>
                      <CardContent>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Typography variant="body2" sx={{ fontWeight: 600, color: '#059669' }}>
                            Total Refund
                          </Typography>
                          <Typography variant="h6" sx={{ fontWeight: 700, color: '#059669' }}>
                            ₹{refundAmounts.total.toLocaleString('en-IN')}
                          </Typography>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              </Grid>

              {/* Items to be Updated */}
              <Grid item xs={12}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                  Items to be Updated ({cancellingItems.length})
                </Typography>
                <Box sx={{
                  maxHeight: 200,
                  overflow: 'auto',
                  borderRadius: '12px',
                  border: isDark
                    ? '1px solid rgba(255, 255, 255, 0.1)'
                    : '1px solid rgba(0, 0, 0, 0.1)',
                  background: isDark
                    ? 'rgba(255, 255, 255, 0.05)'
                    : 'rgba(0, 0, 0, 0.02)',
                  p: 2,
                }}>
                  {cancellingItems.map((item) => (
                    <Box
                      key={item.id}
                      sx={{
                        p: 1.5,
                        mb: 1,
                        borderRadius: '8px',
                        backgroundColor: isDark
                          ? 'rgba(255, 255, 255, 0.02)'
                          : 'rgba(0, 0, 0, 0.01)',
                        border: '1px solid rgba(0, 0, 0, 0.05)',
                      }}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {item.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            HUID: {item.huid} • Weight: {item.weight}g
                          </Typography>
                        </Box>
                        <Box sx={{ textAlign: 'right' }}>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                            Serial Number
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {item.hasConflict ? (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <span style={{ textDecoration: 'line-through', color: '#DC2626' }}>
                                  {item.originalSerialNo}
                                </span>
                                <ArrowForwardIcon sx={{ fontSize: '14px', color: '#6B7280' }} />
                                <span style={{ color: '#059669' }}>
                                  {item.updateData.serialNo}
                                </span>
                              </Box>
                            ) : item.originalSerialNo}
                          </Typography>
                          {item.hasConflict && (
                            <Typography variant="caption" color="#F59E0B" sx={{ display: 'block' }}>
                              Conflict resolved
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </Box>
                  ))}
                </Box>
              </Grid>

              {/* Silver Items */}
              {silverItemsArray.length > 0 && (
                <Grid item xs={12}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, color: '#3B82F6' }}>
                    Silver Items to be Added ({silverItemsArray.length} type(s))
                  </Typography>
                  <Box sx={{
                    maxHeight: 150,
                    overflow: 'auto',
                    borderRadius: '12px',
                    border: '1px solid rgba(59, 130, 246, 0.3)',
                    background: 'rgba(59, 130, 246, 0.05)',
                    p: 2,
                  }}>
                    {silverItemsArray.map((silverGroup) => (
                      <Box
                        key={silverGroup.silverType}
                        sx={{
                          p: 1.5,
                          mb: 1,
                          borderRadius: '8px',
                          backgroundColor: 'rgba(59, 130, 246, 0.08)',
                          border: '1px solid rgba(59, 130, 246, 0.2)',
                        }}
                      >
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 500, color: '#1D4ED8' }}>
                              {silverGroup.silverType.toUpperCase()} Silver
                            </Typography>
                            <Typography variant="caption" sx={{ color: '#3B82F6' }}>
                              {silverGroup.items.length} item(s) • Total Weight: {silverGroup.totalWeight.toFixed(2)}g
                            </Typography>
                          </Box>
                          <Typography variant="caption" sx={{ color: '#3B82F6' }}>
                            Will be added to stock
                          </Typography>
                        </Box>
                      </Box>
                    ))}
                  </Box>
                </Grid>
              )}

              {/* Warning */}
              <Grid item xs={12}>
                <Alert severity="warning" sx={{ borderRadius: '10px' }}>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    Important Notes:
                  </Typography>
                  <Typography variant="caption" component="div">
                    • This action will mark {cancellingItems.length} jewelry item(s) as available in stock
                    {silverItemsArray.length > 0 && (
                      <>
                        <br />
                        • {silverItemsArray.reduce((sum, group) => sum + group.items.length, 0)} silver item(s) will be added back to silver stock
                      </>
                    )}
                    <br />
                    • Serial numbers will be updated if conflicts exist
                    <br />
                    • Refund amount should match the original payment
                    <br />
                    • A cancellation record will be created
                  </Typography>
                </Alert>
              </Grid>
            </Grid>
          )}
        </DialogContent>

        <DialogActions sx={{
          px: 3,
          py: 2,
          borderTop: isDark
            ? '1px solid rgba(255, 255, 255, 0.1)'
            : '1px solid rgba(0, 0, 0, 0.1)',
        }}>
          <Button
            onClick={handleCancelModalClose}
            disabled={isCancelling}
            sx={{
              color: '#6B7280',
              '&:hover': { backgroundColor: 'rgba(107, 114, 128, 0.1)' },
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleConfirmCancellation}
            disabled={isCancelling || refundAmounts.total <= 0}
            startIcon={isCancelling ? <CircularProgress size={16} /> : <CancelIcon />}
            sx={{
              background: 'linear-gradient(135deg, #DC2626, #B91C1C)',
              borderRadius: '10px',
              boxShadow: '0 4px 12px rgba(220, 38, 38, 0.25)',
              fontWeight: 600,
              '&:hover': {
                background: 'linear-gradient(135deg, #B91C1C, #991B1B)',
                boxShadow: '0 6px 20px rgba(220, 38, 38, 0.35)',
              },
              '&.Mui-disabled': {
                background: 'rgba(220, 38, 38, 0.3)',
                color: 'rgba(255, 255, 255, 0.5)',
              },
            }}
          >
            {isCancelling ? 'Cancelling...' : 'Confirm Cancellation'}
          </Button>
        </DialogActions>
      </Dialog>
    </LocalizationProvider>
  );
};

export default React.memo(TransactionHistory);